/**
 * Company Explorer - Unified Company Intelligence Page
 * Shows Company Details, Customers, Competitors, and Timeline for any domain.
 * Two modes: "public" (header/footer, gated) and "dashboard" (sidebar, full access).
 */

const { useState, useEffect, useRef, useCallback, useMemo } = React;

const ExplorerConfig = window.ExplorerConfig || {};
const ExplorerProspector = window.ExplorerProspector || {};

const MODE = ExplorerConfig.MODE || 'public';
const INITIAL_DOMAIN = ExplorerConfig.INITIAL_DOMAIN || '';
const IS_GOOGLEBOT = !!ExplorerConfig.IS_GOOGLEBOT;
const IS_DASHBOARD = !!ExplorerConfig.IS_DASHBOARD;
const INITIAL_SLUG = ExplorerConfig.INITIAL_SLUG || INITIAL_DOMAIN;
const INITIAL_PERSON_SLUG = ExplorerConfig.PERSON_SLUG || '';
const INITIAL_PERSON_LOOKUP = ExplorerConfig.PERSON_LOOKUP || {};
const DASHBOARD_EXPLORER_BASE = '/dashboard/explorer';
const PERSON_PROFILE_ENDPOINT = '/api/v2/employee/profile';
const EXPLORER_PRODUCT_ID = ExplorerConfig.PRODUCT_ID || '';
const EXPLORER_SHORTCUT_CARDS = ExplorerConfig.SHORTCUT_CARDS || {};
const DataExplorerSearch = window.DataExplorerSearch;
const ExplorerApi = window.ExplorerApi;
const useExplorerProspectorSocket = ExplorerProspector.useExplorerProspectorSocket;
const useProfileProspectorJob = ExplorerProspector.useProfileProspectorJob;

// Limits for public vs dashboard vs googlebot
const PUBLIC_VISIBLE = 2;
const GOOGLEBOT_VISIBLE = 10;
const DASHBOARD_PREVIEW = 2;
const PROFILE_CARD_INITIAL_LIMIT = 5;
const PROFILE_CARD_EXPANDED_LIMIT = 10;

const FREE_DOMAINS = ExplorerConfig.FREE_DOMAINS || ['proxycurl.com', 'stripe.com', 'apollo.io'];
const PRICING_DESCRIPTIONS = ExplorerConfig.PRICING_DESCRIPTIONS || {};

// ─── Helpers ──────────────────────────────────────────────────────────

function navigateTo(domain) {
  DataExplorerSearch.navigateToCompany(domain, { dashboard: IS_DASHBOARD });
}

function navigateToPersonLookup(params) {
  DataExplorerSearch.navigateToPersonLookup(params);
}

const isTwimgUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname === 'twimg.com' || parsed.hostname.endsWith('.twimg.com');
  } catch {
    return false;
  }
};

const proxiedProfilePicUrl = (url) => {
  if (!url || !IS_DASHBOARD || !isTwimgUrl(url)) return url;
  return `/dashboard/explorer/api/person-image-proxy?url=${encodeURIComponent(url)}`;
};

function navigateToPersonSlug(slug) {
  DataExplorerSearch.navigateToPersonSlug(slug);
}

const PERSON_LOOKUP_EMAIL_REF_PARAM = 'lookup_ref';
const PERSON_LOOKUP_EMAIL_TTL_MS = 15 * 60 * 1000;

const createPersonLookupRef = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const personLookupEmailKey = (slug, ref) => (
  `explorer_person_lookup_email:${encodeURIComponent(slug || '')}:${encodeURIComponent(ref || '')}`
);

const storePersonLookupEmail = (slug, workEmail) => {
  const cleanSlug = String(slug || '').trim();
  const cleanEmail = String(workEmail || '').trim();
  if (!cleanSlug || !cleanEmail || !cleanEmail.includes('@') || !window.sessionStorage) return null;

  const ref = createPersonLookupRef();
  try {
    window.sessionStorage.setItem(
      personLookupEmailKey(cleanSlug, ref),
      JSON.stringify({ work_email: cleanEmail, created_at: Date.now() })
    );
    return ref;
  } catch {
    return null;
  }
};

const addPersonLookupRefToUrl = (rawUrl, ref) => {
  if (!rawUrl || !ref) return rawUrl;
  try {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.set(PERSON_LOOKUP_EMAIL_REF_PARAM, ref);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const separator = rawUrl.includes('?') ? '&' : '?';
    return `${rawUrl}${separator}${PERSON_LOOKUP_EMAIL_REF_PARAM}=${encodeURIComponent(ref)}`;
  }
};

const extractPersonSlugFromRedirect = (redirectUrl) => {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    const marker = '/dashboard/explorer/person/';
    const index = url.pathname.indexOf(marker);
    if (index === -1) return '';
    return decodeURIComponent(url.pathname.slice(index + marker.length).split('/')[0] || '');
  } catch {
    return '';
  }
};

const removePersonLookupRefFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PERSON_LOOKUP_EMAIL_REF_PARAM)) return;
    url.searchParams.delete(PERSON_LOOKUP_EMAIL_REF_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // URL cleanup is best-effort; the storage token is still consumed below.
  }
};

const consumePersonLookupEmail = (slug) => {
  const cleanSlug = String(slug || '').trim();
  if (!cleanSlug || !window.sessionStorage) return null;

  let ref = '';
  try {
    ref = new URLSearchParams(window.location.search).get(PERSON_LOOKUP_EMAIL_REF_PARAM) || '';
  } catch {
    ref = '';
  }
  if (!ref) return null;

  const storageKey = personLookupEmailKey(cleanSlug, ref);
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    window.sessionStorage.removeItem(storageKey);
    removePersonLookupRefFromUrl();
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.created_at || 0);
    const workEmail = String(parsed?.work_email || '').trim();
    if (!workEmail || !workEmail.includes('@')) return null;
    if (!createdAt || Date.now() - createdAt > PERSON_LOOKUP_EMAIL_TTL_MS) return null;
    return workEmail;
  } catch {
    return null;
  }
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffDay === 0) return 'Today';
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getDateLabel(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (itemDate.getTime() === today.getTime()) return 'Today';
  if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function getIndustryLabel(code) {
  if (!code) return null;
  if (window.GICS_CODES && window.GICS_CODES[code]) return window.GICS_CODES[code];
  return null;
}

const COMPANY_TYPE_LABELS = {
  'PRIVATELY_HELD': 'Private',
  'PUBLIC_COMPANY': 'Public',
  'SELF_EMPLOYED': 'Self-Employed',
  'GOVERNMENT_AGENCY': 'Government',
  'NON_PROFIT': 'Non-Profit',
  'SOLE_PROPRIETORSHIP': 'Sole Proprietorship',
  'PARTNERSHIP': 'Partnership',
  'SELF_OWNED': 'Self-Owned',
};

function getCompanyTypeLabel(type) {
  if (!type) return null;
  return COMPANY_TYPE_LABELS[type] || type;
}

function formatAddress(addr) {
  if (!addr) return null;
  const parts = [addr.city, addr.state, addr.country].filter(Boolean);
  return parts.join(', ');
}

function formatCurrency(val) {
  if (val == null) return null;
  if (val >= 1e9) return '$' + (val / 1e9).toFixed(1) + 'B';
  if (val >= 1e6) return '$' + (val / 1e6).toFixed(0) + 'M';
  return '$' + val.toLocaleString();
}

// Extract a human-readable handle from a social URL.
// Returns null if the URL is empty / unparseable / has no path segment.
// `platform` controls the @ prefix: x/instagram conventionally show one,
// facebook (page slugs/numeric IDs) does not.
function extractSocialHandle(url, platform) {
  if (!url) return null;
  let path;
  try {
    path = new URL(url).pathname || '';
  } catch (_e) {
    const m = String(url).match(/^[^?#]+/);
    path = m ? m[0].replace(/^https?:\/\/[^/]+/, '') : '';
  }
  const segment = path.split('/').filter(Boolean)[0];
  if (!segment) return null;
  const cleaned = decodeURIComponent(segment).replace(/^@/, '');
  if (platform === 'facebook') return cleaned;
  return '@' + cleaned;
}

// ─── Shared Sub-Components ────────────────────────────────────────────

const DataSectionHeader = ({ icon, title, expanded, onToggle, actions }) => (
  <div className="flex items-center justify-between">
    <button onClick={onToggle} className="flex items-center gap-2 group">
      <i className={`ph ph-${icon} text-lg text-purple-600`}></i>
      <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>
      <i className={`ph ph-caret-${expanded ? 'up' : 'down'} text-zinc-400 group-hover:text-zinc-600 transition-colors`}></i>
    </button>
    {actions && <div>{actions}</div>}
  </div>
);

const CreditActionDropdown = ({
  label,
  icon = 'ph-eye',
  creditCost,
  creditDescription,
  onReveal,
  loading,
  variant = 'soft',
  align = 'right',
  alwaysLabel,
  alwaysHint,
  onceLabel,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const creditText = creditDescription || `${creditCost} credit${creditCost !== 1 ? 's' : ''}`;
  const buttonClass = variant === 'primary'
    ? (loading ? 'text-white bg-purple-400 cursor-not-allowed' : 'text-white bg-purple-600 hover:bg-purple-700')
    : (loading ? 'text-purple-400 bg-purple-50 border border-purple-100 cursor-not-allowed' : 'text-purple-600 bg-purple-50 border border-purple-200 hover:bg-purple-100');
  const menuAlignClass = align === 'left' ? 'left-0' : 'right-0';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !loading && setOpen(!open)}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${buttonClass}`}
      >
        {loading ? (
          <>
            <i className="ph ph-spinner-gap animate-spin text-sm"></i>
            Loading...
          </>
        ) : (
          <>
            <i className={`ph ${icon} text-sm`}></i>
            {label}
            <i className={`ph ph-caret-${open ? 'up' : 'down'} text-xs`}></i>
          </>
        )}
      </button>
      {open && (
        <div className={`absolute ${menuAlignClass} mt-1 w-72 bg-white border border-zinc-200 rounded-xl shadow-lg z-10 p-4 space-y-3`}>
          <div className="flex items-center gap-2 text-sm text-zinc-600">
            <i className="ph ph-coins text-amber-500"></i>
            <span><strong>{creditText}</strong></span>
          </div>
          <button
            onClick={() => {
              onReveal(true);
              setOpen(false);
            }}
            className="w-full px-3 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors text-left"
          >
            {alwaysLabel}
            <div className="text-xs text-purple-200 mt-0.5">{alwaysHint}</div>
          </button>
          <button
            onClick={() => { onReveal(false); setOpen(false); }}
            className="w-full px-3 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
          >
            {onceLabel}
          </button>
        </div>
      )}
    </div>
  );
};

const DashboardCTADropdown = ({ label, creditCost, creditDescription, onReveal, loading }) => (
  <CreditActionDropdown
    label={label}
    creditCost={creditCost}
    creditDescription={creditDescription}
    onReveal={onReveal}
    loading={loading}
    variant="soft"
    align="right"
    alwaysLabel="Always Reveal Company Details Profile Data"
    alwaysHint={`I understand that it costs ${creditDescription || `${creditCost} credits`} every page load`}
    onceLabel="Reveal Data this time only"
  />
);

const PrimaryCTADropdown = ({ label, icon, creditCost, creditDescription, onReveal, loading }) => {
  return (
    <CreditActionDropdown
      label={label}
      icon={icon || 'ph-eye'}
      creditCost={creditCost}
      creditDescription={creditDescription}
      onReveal={onReveal}
      loading={loading}
      variant="primary"
      align="left"
      alwaysLabel={`Always run ${label}`}
      alwaysHint={`I understand it costs ${creditDescription || `${creditCost} credits`} every time`}
      onceLabel="Run this time only"
    />
  );
};

const DashboardRevealButton = ({ label, creditText, onReveal, loading }) => (
  <button
    onClick={onReveal}
    disabled={loading}
    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50"
  >
    {loading ? (
      <><i className="ph ph-spinner-gap animate-spin text-sm"></i>Loading...</>
    ) : (
      <>
        <i className="ph ph-eye text-sm"></i>
        {label}
        {creditText && <span className="text-purple-400 text-xs">({creditText})</span>}
      </>
    )}
  </button>
);

const OpenInProspectorFooter = ({ shown, total, label, lastRefreshed, onOpen, loading }) => (
  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-xs text-zinc-500">
    <span>
      Showing {shown} of {total} {label}
      {lastRefreshed && <span className="text-zinc-400"> · last refreshed {lastRefreshed}</span>}
    </span>
    <button
      type="button"
      onClick={onOpen}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors font-medium"
    >
      <i className={`ph ${loading ? 'ph-spinner animate-spin' : 'ph-table'} text-sm`}></i>
      {loading ? 'Opening...' : 'Open in Prospector'}
    </button>
  </div>
);

const ProspectorSheetNotice = ({ href }) => (
  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
    <i className="ph ph-table mr-1.5"></i>
    Results will be saved to{' '}
    {href ? (
      <a href={href} className="font-medium text-emerald-700 hover:underline">
        this Prospector spreadsheet
      </a>
    ) : (
      <span className="font-medium">a Prospector spreadsheet</span>
    )}
    .
  </div>
);

// ─── Skeleton loading primitives ──────────────────────────────────────
// Animated content-shaped placeholders. Pass `animate={false}` to render
// the same shape as a static "preview" (used for empty states).

const SkeletonBar = ({ className = '', animate = true }) => (
  <div className={`${animate ? 'animate-shimmer' : 'bg-zinc-200/60'} rounded-md ${className}`} />
);

const SkeletonRow = ({ variant = 'avatarText', animate = true }) => {
  if (variant === 'logoText') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2.5">
        <SkeletonBar animate={animate} className="h-9 w-9 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-1.5">
          <SkeletonBar animate={animate} className="h-3 w-1/2" />
          <SkeletonBar animate={animate} className="h-2.5 w-1/3" />
        </div>
        <SkeletonBar animate={animate} className="h-3 w-16" />
      </div>
    );
  }
  if (variant === 'timelineItem') {
    return (
      <div className="flex gap-3">
        <div className="flex flex-col items-center pt-1">
          <SkeletonBar animate={animate} className="h-2.5 w-2.5 rounded-full" />
          <div className="mt-1 w-px flex-1 bg-zinc-200" />
        </div>
        <div className="flex-1 min-w-0 space-y-1.5 pb-4">
          <SkeletonBar animate={animate} className="h-3 w-2/3" />
          <SkeletonBar animate={animate} className="h-2.5 w-full" />
          <SkeletonBar animate={animate} className="h-2.5 w-4/5" />
        </div>
      </div>
    );
  }
  // avatarText (default)
  return (
    <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2.5">
      <SkeletonBar animate={animate} className="h-9 w-9 rounded-full flex-shrink-0" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <SkeletonBar animate={animate} className="h-3 w-1/2" />
        <SkeletonBar animate={animate} className="h-2.5 w-2/3" />
      </div>
    </div>
  );
};

const SkeletonCardBody = ({ title, elapsedSeconds, rows = 4, variant = 'avatarText', href }) => (
  <div className="space-y-3 rounded-xl border border-zinc-200 bg-zinc-50/60 p-4">
    {(title || elapsedSeconds != null) && (
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-2 font-medium text-zinc-700">
          <i className="ph ph-circle-notch animate-spin text-purple-600"></i>
          {title}
        </span>
        {elapsedSeconds != null && (
          <span className="tabular-nums text-zinc-500">{elapsedSeconds}s</span>
        )}
      </div>
    )}
    <div className={variant === 'timelineItem' ? '' : 'space-y-2'}>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} variant={variant} />
      ))}
    </div>
    {href && (
      <div className="pt-1 text-center">
        <a href={href} className="text-xs font-medium text-zinc-500 hover:text-zinc-800 hover:underline">
          View progress in Prospector
        </a>
      </div>
    )}
  </div>
);

const InlineLoadingChip = ({ title, elapsedSeconds }) => (
  <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/95 px-3 py-1.5 text-xs font-medium text-zinc-700 shadow-sm">
    <i className="ph ph-circle-notch animate-spin text-purple-600"></i>
    <span>{title}</span>
    {elapsedSeconds != null && <span className="tabular-nums text-zinc-500">{elapsedSeconds}s</span>}
  </div>
);

const NoProfileFoundPanel = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3.5">
    <div className="flex items-start gap-2.5">
      <i className="ph ph-magnifying-glass text-base text-amber-600 mt-0.5 flex-shrink-0"></i>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-950">No profile found</p>
        <p className="mt-0.5 text-xs text-amber-900/90 break-words">
          We completed the lookup, but could not find a matching person profile. This lookup used 3 credits.
        </p>
        <a
          href={DASHBOARD_EXPLORER_BASE}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-950 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors no-btn-shadow"
        >
          <i className="ph ph-arrow-left text-sm"></i>
          Try another lookup
        </a>
      </div>
    </div>
  </div>
);

// Shared error panel. Detects out-of-credit errors and shows a billing CTA;
// for any other error, surfaces a retry button + support email.
const isCreditError = (msg) => /\bcredit/i.test(String(msg || ''));

const ErrorPanel = ({ error, onRetry, retrying = false, compact = false }) => {
  if (!error) return null;
  const text = String(error);
  const credit = isCreditError(text);
  const padding = compact ? 'px-3 py-2.5' : 'px-4 py-3.5';
  return (
    <div className={`rounded-xl border border-red-200 bg-red-50/80 ${padding}`}>
      <div className="flex items-start gap-2.5">
        <i className={`ph ${credit ? 'ph-coin' : 'ph-warning-circle'} text-base text-red-500 mt-0.5 flex-shrink-0`}></i>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-900">
            {credit ? 'Out of credits' : 'Something went wrong'}
          </p>
          <p className="mt-0.5 text-xs text-red-800/90 break-words">{text}</p>
          {credit ? (
            <a
              href="/dashboard/billing#plans"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors no-btn-shadow"
            >
              <i className="ph ph-coins text-sm"></i>
              Buy credits
            </a>
          ) : (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  disabled={retrying}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-zinc-800 rounded-lg hover:bg-zinc-900 transition-colors disabled:opacity-60 no-btn-shadow"
                >
                  <i className={`ph ${retrying ? 'ph-circle-notch animate-spin' : 'ph-arrow-clockwise'} text-sm`}></i>
                  {retrying ? 'Retrying…' : 'Retry'}
                </button>
              )}
              <span className="text-xs text-red-800/80">
                Still stuck? Email{' '}
                <a href="mailto:hello@nubela.co" className="font-medium underline hover:text-red-900">
                  hello@nubela.co
                </a>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RevealLoadingPanel = ({ title, subtitle }) => (
  <div className="text-center px-5 py-6 max-w-md">
    <div className="relative inline-flex items-center justify-center w-12 h-12 mb-3">
      <span className="absolute inset-0 rounded-full bg-purple-100"></span>
      <i className="ph ph-spinner-gap animate-spin text-2xl text-purple-600 relative"></i>
    </div>
    <p className="text-sm font-semibold text-zinc-900 mb-1">{title}</p>
    {subtitle && <p className="text-xs text-zinc-500 leading-relaxed">{subtitle}</p>}
  </div>
);

const ProspectorProgressPanel = ({ title, href, elapsedSeconds, rows = 4, variant = 'avatarText' }) => (
  <div className="space-y-3">
    <ProspectorSheetNotice href={href} />
    <SkeletonCardBody title={title} elapsedSeconds={elapsedSeconds} rows={rows} variant={variant} />
  </div>
);

const ProspectorQueuedFooter = ({ href, label = 'Open spreadsheet' }) => {
  if (!href) return null;
  return (
    <div className="mt-3">
      <a
        href={href}
        className="flex w-full items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-zinc-700 border border-zinc-200 bg-zinc-50 rounded-lg hover:bg-zinc-100 hover:border-zinc-300 hover:text-zinc-900 transition-colors no-btn-shadow"
      >
        <i className="ph ph-table text-sm"></i>
        {label}
      </a>
    </div>
  );
};

const ProfileCardResultsFooter = ({
  shown,
  total,
  label,
  href,
  canShowMore,
  onShowMore,
  onViewAll,
  viewAllLoading,
  viewAllLabel = 'View all in Prospector',
}) => {
  if (!total && !href && !onViewAll) return null;
  const showViewAllButton = typeof onViewAll === 'function';
  return (
    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-xs text-zinc-500">
      {total > 0 ? (
        <span>Showing {shown} of {total} {label}</span>
      ) : (
        <span></span>
      )}
      <div className="flex items-center gap-2">
        {canShowMore && (
          <button
            type="button"
            onClick={onShowMore}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <i className="ph ph-caret-down text-sm"></i>
            Show more
          </button>
        )}
        {showViewAllButton ? (
          <button
            type="button"
            onClick={onViewAll}
            disabled={viewAllLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 font-medium text-zinc-700 border border-zinc-200 bg-zinc-50 rounded-lg hover:bg-zinc-100 hover:border-zinc-300 hover:text-zinc-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i className={`ph ${viewAllLoading ? 'ph-spinner animate-spin' : 'ph-table'} text-sm`}></i>
            {viewAllLabel}
          </button>
        ) : href && (
          <a
            href={href}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 font-medium text-zinc-700 border border-zinc-200 bg-zinc-50 rounded-lg hover:bg-zinc-100 hover:border-zinc-300 hover:text-zinc-900 transition-colors"
          >
            <i className="ph ph-table text-sm"></i>
            {viewAllLabel}
          </a>
        )}
      </div>
    </div>
  );
};

const ProfileCardLoadingOverlay = ({ title, href, elapsedSeconds }) => (
  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/70 backdrop-blur-[2px]">
    <InlineLoadingChip title={title} elapsedSeconds={elapsedSeconds} />
    {href && (
      <a href={href} className="text-xs font-medium text-zinc-500 hover:text-zinc-800 hover:underline">
        View progress in Prospector
      </a>
    )}
  </div>
);

const BlurredOverlay = ({ type, companyName }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-zinc-50 via-zinc-50/90 to-transparent">
      <div className="text-center p-6">
        <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ph ph-lock-simple text-2xl text-purple-600"></i>
        </div>
        <h3 className="text-lg font-semibold text-zinc-900 mb-2">Discover {companyName}'s {type}</h3>
        <p className="text-zinc-600 mb-4 max-w-md">
          Sign up for free to view all {type} of {companyName}.
          <br />
          <span className="text-purple-600 font-medium">New accounts include trial credits to get started.</span>
        </p>
        <a
          href="/auth/register"
          className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors"
        >
          <i className="ph ph-user-plus mr-2"></i>
          Create Free Account
        </a>
        <p className="mt-3 text-sm text-zinc-500">
          Already have an account? <a href="/auth/login" className="text-purple-600 hover:underline">Sign in</a>
        </p>
      </div>
    </div>
  );
};

// Credit confirmation modal (inline, for CTA actions)
// ─── Curl Command Box ────────────────────────────────────────────────

const CurlCommandBox = ({ endpoint, domain, queryParam = 'website' }) => {
  const apiKey = IS_DASHBOARD ? (ExplorerConfig.API_KEY || 'YOUR_API_KEY') : 'demo-api-key';
  const origin = window.location.origin;
  const queryValue = domain || '';
  const curlCmd = `curl -X GET "${origin}${endpoint}?${queryParam}=${encodeURIComponent(queryValue)}" \\\n  -H "Authorization: Bearer ${apiKey}"`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(curlCmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-800 border-b border-zinc-700">
        <div className="flex items-center gap-2">
          <i className="ph-fill ph-terminal text-green-400"></i>
          <span className="text-xs font-medium text-zinc-400">{endpoint}</span>
        </div>
        <button onClick={handleCopy} className="text-xs text-zinc-400 hover:text-white transition-colors flex items-center gap-1">
          <i className={`ph ${copied ? 'ph-check' : 'ph-copy'} text-sm`}></i>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="p-4">
        <pre className="text-sm text-zinc-300 font-mono whitespace-pre-wrap">
          <span className="text-green-400">curl</span>{" -X GET \\\n  "}
          <span className="text-blue-400">"{origin}{endpoint}?{queryParam}={encodeURIComponent(queryValue)}"</span>{" \\\n  "}
          -H <span className="text-amber-400">"Authorization: Bearer {apiKey}"</span>
        </pre>
      </div>
      {!IS_DASHBOARD && (
        <div className="px-4 pb-3">
          <p className="text-xs text-zinc-500">Replace <code className="text-amber-400">demo-api-key</code> with your API key</p>
        </div>
      )}
    </div>
  );
};

// ─── Mini Table Components (for preview) ──────────────────────────────

const CustomerMiniTable = ({ customers, blurred }) => {
  const normalizeDomain = window.normalizeDomain || ((x) => x || null);
  return (
  <div className={`bg-white border border-zinc-200 rounded-xl overflow-hidden ${blurred ? 'blur-sm select-none pointer-events-none' : ''}`}>
    <table className="w-full">
      <thead>
        <tr className="bg-zinc-50 border-b border-zinc-200">
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Company</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider hidden sm:table-cell">Website</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Description</th>
        </tr>
      </thead>
      <tbody>
        {customers.map((c, i) => {
          const customerDomain = normalizeDomain(c.website || c.domain);
          return (
            <tr key={i} className="border-b border-zinc-100 last:border-b-0 hover:bg-purple-50/50 transition-colors">
              <td className="px-4 py-3">
                <a href={(IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/') + encodeURIComponent(customerDomain || c.slug || '')} className="flex items-center gap-2 group">
                  {window.CompanyAvatar ? (
                    <CompanyAvatar name={c.name} domain={customerDomain} size="sm" />
                  ) : (
                    <div className="w-7 h-7 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-xs">{(c.name || '?').charAt(0)}</div>
                  )}
                  <span className="font-medium text-sm text-zinc-900 group-hover:text-purple-600 transition-colors">{c.name}</span>
                </a>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell">
                <span className="text-xs text-zinc-400">{customerDomain}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-xs text-zinc-500 line-clamp-1">{c.description}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};

const FundingMiniTable = ({ rounds, blurred }) => (
  <div className={`bg-white border border-zinc-200 rounded-xl overflow-hidden ${blurred ? 'blur-sm select-none pointer-events-none' : ''}`}>
    <table className="w-full">
      <thead>
        <tr className="bg-zinc-50 border-b border-zinc-200">
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Round</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Amount</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Investors</th>
        </tr>
      </thead>
      <tbody>
        {rounds.map((r, i) => (
          <tr key={i} className="border-b border-zinc-100 last:border-b-0 hover:bg-purple-50/50 transition-colors">
            <td className="px-4 py-3">
              <span className="inline-flex items-center px-2 py-0.5 bg-emerald-50 border border-emerald-100 rounded-full text-xs font-medium text-emerald-700">{r.round}</span>
            </td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <span className="text-xs text-zinc-500">{r.date}</span>
            </td>
            <td className="px-4 py-3">
              <span className="text-sm font-semibold text-zinc-900 tabular-nums">{r.amount}</span>
            </td>
            <td className="px-4 py-3">
              <span className="text-xs text-zinc-600 line-clamp-1">{r.investors}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CompetitorMiniTable = ({ competitors, blurred }) => (
  <div className={`bg-white border border-zinc-200 rounded-xl overflow-hidden ${blurred ? 'blur-sm select-none pointer-events-none' : ''}`}>
    <table className="w-full">
      <thead>
        <tr className="bg-zinc-50 border-b border-zinc-200">
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Company</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider hidden sm:table-cell">Website</th>
          <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Competition Reason</th>
        </tr>
      </thead>
      <tbody>
        {competitors.map((c, i) => (
          <tr key={i} className="border-b border-zinc-100 last:border-b-0 hover:bg-purple-50/50 transition-colors">
            <td className="px-4 py-3">
              <a href={(IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/') + encodeURIComponent(c.domain)} className="flex items-center gap-2 group">
                {window.CompanyAvatar ? (
                  <CompanyAvatar name={c.name || c.domain} domain={c.domain} size="sm" />
                ) : (
                  <div className="w-7 h-7 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-xs">{(c.name || c.domain || '?').charAt(0)}</div>
                )}
                <span className="font-medium text-sm text-zinc-900 group-hover:text-purple-600 transition-colors">{c.name || c.domain}</span>
              </a>
            </td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <span className="text-xs text-zinc-400">{c.domain}</span>
            </td>
            <td className="px-4 py-3">
              <span className="text-xs text-zinc-500 line-clamp-1">{formatCompetitionReason(c.competition_reason)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BLURRED_CUSTOMER_PLACEHOLDERS = [
  { name: 'Company A', domain: 'example.com', description: 'A technology company...' },
  { name: 'Company B', domain: 'sample.org', description: 'Enterprise software provider...' },
  { name: 'Company C', domain: 'demo.io', description: 'Cloud infrastructure platform...' },
];

const BLURRED_COMPETITOR_PLACEHOLDERS = [
  { name: 'Competitor A', domain: 'rival1.com', competition_reason: 'organic_keyword_overlap' },
  { name: 'Competitor B', domain: 'rival2.com', competition_reason: 'product_overlap' },
  { name: 'Competitor C', domain: 'rival3.com', competition_reason: 'organic_keyword_overlap' },
];

const BLURRED_FUNDING_PLACEHOLDERS = [
  { round: 'Series B', date: 'Mar 2024', amount: '$48M', investors: 'Northstar Ventures, Summit Capital' },
  { round: 'Series A', date: 'Aug 2022', amount: '$18M', investors: 'Peak Fund, Horizon Partners' },
  { round: 'Seed', date: 'Jan 2021', amount: '$4M', investors: 'Founders Collective' },
];

const sheetRowsToCustomers = (rows) => (rows || []).map((row) => ({
  name: row.name || row.domain || 'Unknown company',
  website: row.domain,
  domain: row.domain,
  description: row.relationship_type || (row.customer_of ? `Customer of ${row.customer_of}` : ''),
})).filter(row => row.domain || row.name);

const sheetRowsToCompetitors = (rows) => (rows || []).map((row) => ({
  name: row.name || row.domain || 'Unknown company',
  domain: row.domain,
  competition_reason: row.competition_reason || '',
})).filter(row => row.domain || row.name);

const formatFundingDateLabel = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const formatRoundLabel = (value) => {
  if (!value) return 'Unknown';
  return String(value)
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const sheetRowsToFundingRounds = (rows) => {
  const grouped = new Map();
  (rows || []).forEach((row) => {
    const key = [row.stage || '', row.date || '', row.amount_usd || ''].join('|');
    const existing = grouped.get(key) || {
      round: formatRoundLabel(row.stage),
      date: formatFundingDateLabel(row.date),
      amount: formatCurrency(row.amount_usd) || 'Undisclosed',
      investors: [],
    };
    if (row.investor_name && !existing.investors.includes(row.investor_name)) {
      existing.investors.push(row.investor_name);
    }
    grouped.set(key, existing);
  });
  return Array.from(grouped.values()).map((round) => ({
    ...round,
    investors: round.investors.slice(0, 4).join(', ') || 'Unknown',
  }));
};

const sheetRowsToTimelineItems = (rows) => (rows || []).map((row) => ({
  category: row.source === 'x' || row.kind === 'X post' ? 'x_post' : 'blog_post',
  pub_date: row.published,
  link: row.url,
  title: row.title || 'Company update',
  description: row.description || '',
})).filter(item => item.title || item.link);

// ─── Company Detail Card ─────────────────────────────────────────────

const CompanyDetailCard = ({ companySummary, summaryLoading, fullProfile, onRevealDetails, revealLoading, revealError, mode, domain, autoRevealing }) => {
  const [descExpanded, setDescExpanded] = useState(false);
  const [showCurl, setShowCurl] = useState(false);

  const details = companySummary?.company || {};
  const revealed = !!fullProfile;

  // Full profile data (only when revealed/paid)
  const profile = fullProfile?.profile || {};

  // Merge full profile with summary - full profile takes precedence when available
  const effectiveDescription = profile.description || details.description;
  const effectiveFoundedYear = profile.founded_year || details.founded_year;
  const allSpecialties = (profile.specialties && profile.specialties.length > 0)
    ? profile.specialties
    : (details.specialties || []);
  const firstSpecialty = allSpecialties.length > 0 ? allSpecialties[0] : null;

  // Description with show more/less toggle
  const descTruncLen = 200;
  const descIsTruncatable = effectiveDescription && effectiveDescription.length > descTruncLen;
  const descDisplay = descIsTruncatable && !descExpanded
    ? effectiveDescription.slice(0, descTruncLen) + '...'
    : effectiveDescription;

  // Always-visible fields from free summary
  const industryLabel = getIndustryLabel(details.industry);
  const companyTypeLabel = getCompanyTypeLabel(details.company_type);

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6">
      {/* Always visible: Logo, Name, Website, Description, Founded, 1 Specialty */}
      {summaryLoading ? (
        <div className="flex items-start gap-4">
          <SkeletonBar className="h-14 w-14 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <SkeletonBar className="h-6 w-48" />
            <SkeletonBar className="h-3 w-full" />
            <SkeletonBar className="h-3 w-4/5" />
            <div className="pt-1">
              <SkeletonBar className="h-3 w-24" />
            </div>
            <div className="pt-1">
              <SkeletonBar className="h-5 w-20 rounded-full" />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-4">
          {window.CompanyAvatar ? (
            <CompanyAvatar name={details.name || domain} domain={domain} size="xl" />
          ) : (
            details.logo_url ? <img src={details.logo_url} alt={details.name} className="w-14 h-14 rounded-xl object-contain" /> :
            <div className="w-14 h-14 bg-zinc-100 rounded-xl flex items-center justify-center text-zinc-400 font-bold text-xl">{(details.name || domain || '?').charAt(0).toUpperCase()}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="text-xl font-bold text-zinc-900">{details.name || domain}</h1>
              {details.website && (
                <a href={details.website} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-purple-600">
                  <i className="ph ph-arrow-square-out text-sm"></i>
                </a>
              )}
              <button
                onClick={() => setShowCurl(!showCurl)}
                className="ml-auto text-xs text-zinc-400 hover:text-purple-600 transition-colors font-mono"
                title="Show API commands"
              >
                &lt;/&gt;
              </button>
            </div>
            <p className="text-sm text-zinc-600 mb-1">
              {descDisplay}
              {descIsTruncatable && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="text-purple-600 hover:text-purple-700 ml-1 text-xs font-medium"
                >
                  {descExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </p>

            {/* Stats row: founded year */}
            <div className="flex flex-wrap gap-3 text-sm text-zinc-500 mt-2">
              {effectiveFoundedYear && (
                <span className="flex items-center gap-1">
                  <i className="ph ph-calendar-blank text-zinc-400"></i>
                  Founded {effectiveFoundedYear}
                </span>
              )}
            </div>

            {firstSpecialty && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className="inline-flex items-center px-2 py-0.5 bg-zinc-100 rounded-full text-xs text-zinc-600">
                  {firstSpecialty}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Curl command box */}
      {showCurl && (
        <div className="mt-4">
          <CurlCommandBox endpoint="/api/v1/company/details" domain={domain} />
          <div className="mt-2">
            <CurlCommandBox endpoint="/api/v1/company/employee-count" domain={domain} />
          </div>
        </div>
      )}

      {/* Blurred/revealed fields section */}
      <div className="mt-4 pt-4 border-t border-zinc-100">
        {summaryLoading && !revealed && !autoRevealing && !revealLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-zinc-50 rounded-lg px-3 py-2 space-y-1.5">
                <SkeletonBar className="h-2.5 w-1/2" />
                <SkeletonBar className="h-3 w-2/3" />
              </div>
            ))}
          </div>
        ) : !revealed ? (
          isCreditError(revealError) ? (
            <ErrorPanel
              error={revealError}
              onRetry={onRevealDetails}
              retrying={revealLoading}
            />
          ) : (autoRevealing || revealLoading) ? (
            <div className="flex items-center justify-center py-6">
              <RevealLoadingPanel
                title="Loading company details"
                subtitle="Pulling tagline, executives, social presence and headcount…"
              />
            </div>
          ) : (
          <div className="relative">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 select-none pointer-events-none">
              {details.tagline && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Tagline</div>
                  <div className="text-sm font-medium text-zinc-700 blur-sm select-none">{details.tagline}</div>
                </div>
              )}
              {companyTypeLabel && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Company Type</div>
                  <div className="text-sm font-medium text-zinc-700 blur-sm select-none">{companyTypeLabel}</div>
                </div>
              )}
              {industryLabel && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Industry</div>
                  <div className="text-sm font-medium text-zinc-700 blur-sm select-none">{industryLabel}</div>
                </div>
              )}
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Employees</div>
                <div className="text-sm font-medium text-zinc-700 blur-sm select-none">~1,000</div>
              </div>
              <div className="bg-zinc-50 rounded-lg px-3 py-2 sm:col-span-2 lg:col-span-1">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">Social</div>
                <div className="flex flex-col gap-1 blur-sm select-none">
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700">
                    <i className="ph ph-facebook-logo text-base text-zinc-400"></i>
                    <span>@example</span>
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700">
                    <i className="ph ph-x-logo text-base text-zinc-400"></i>
                    <span>@example</span>
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700">
                    <i className="ph ph-instagram-logo text-base text-zinc-400"></i>
                    <span>@example</span>
                  </span>
                </div>
              </div>
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Executives</div>
                <div className="text-sm font-medium text-zinc-700 blur-sm select-none">John Doe</div>
                <div className="text-xs text-zinc-500 blur-sm select-none">CEO</div>
              </div>
            </div>
            {revealError && (
              <div className="relative z-10 mb-3">
                <ErrorPanel
                  error={revealError}
                  onRetry={onRevealDetails}
                  retrying={revealLoading}
                  compact
                />
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              {mode === 'dashboard' ? (
                <DashboardCTADropdown
                  label="Reveal Details"
                  creditCost={4}
                  onReveal={(always) => {
                    if (always) localStorage.setItem('explorer_always_reveal_details', 'true');
                    onRevealDetails();
                  }}
                  loading={revealLoading}
                />
              ) : (
                <a
                  href="/auth/register"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <i className="ph ph-lock-simple text-sm"></i>
                  Sign up to reveal
                </a>
              )}
            </div>
          </div>
          )
        ) : (
          <div className="space-y-4">
            {(profile.tagline || details.tagline) && (
              <p className="text-sm text-zinc-500 italic">{profile.tagline || details.tagline}</p>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {companyTypeLabel && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Company Type</div>
                  <div className="text-sm font-medium text-zinc-700">{companyTypeLabel}</div>
                </div>
              )}
              {(profile.industry_name || industryLabel) && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Industry</div>
                  <div className="text-sm font-medium text-zinc-700">{profile.industry_name || industryLabel}</div>
                </div>
              )}
              {profile.employee_count && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Employees</div>
                  <div className="text-sm font-medium text-zinc-700 tabular-nums">~{profile.employee_count.toLocaleString()}</div>
                </div>
              )}
              {(profile.facebook_url || profile.twitter_url || profile.instagram_url) && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2 sm:col-span-2 lg:col-span-1">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">Social</div>
                  <div className="flex flex-col gap-1">
                    {profile.facebook_url && (
                      <a
                        href={profile.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-blue-600 transition-colors no-btn-shadow"
                      >
                        <i className="ph ph-facebook-logo text-base text-zinc-400 group-hover:text-blue-600 transition-colors"></i>
                        <span className="truncate">{extractSocialHandle(profile.facebook_url, 'facebook') || 'Facebook'}</span>
                      </a>
                    )}
                    {profile.twitter_url && (
                      <a
                        href={profile.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors no-btn-shadow"
                      >
                        <i className="ph ph-x-logo text-base text-zinc-400 group-hover:text-zinc-900 transition-colors"></i>
                        <span className="truncate">{extractSocialHandle(profile.twitter_url, 'x') || 'X'}</span>
                      </a>
                    )}
                    {profile.instagram_url && (
                      <a
                        href={profile.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-pink-600 transition-colors no-btn-shadow"
                      >
                        <i className="ph ph-instagram-logo text-base text-zinc-400 group-hover:text-pink-600 transition-colors"></i>
                        <span className="truncate">{extractSocialHandle(profile.instagram_url, 'instagram') || 'Instagram'}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
              {profile.executives && profile.executives.length > 0 && profile.executives.slice(0, 3).map((exec, i) => (
                <div key={i} className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">{exec.role || 'Executive'}</div>
                  <div className="text-sm font-medium text-zinc-700">{exec.name}</div>
                  <div className="text-xs text-zinc-500">{exec.title}</div>
                </div>
              ))}
              {profile.addresses && profile.addresses.length > 0 && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Headquarters</div>
                  <div className="text-sm font-medium text-zinc-700">{formatAddress(profile.addresses[0])}</div>
                </div>
              )}
              {profile.hostnames && profile.hostnames.length > 0 && (
                <div className="bg-zinc-50 rounded-lg px-3 py-2 sm:col-span-2 lg:col-span-1">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">Hostnames</div>
                  <div className="flex flex-wrap gap-1">
                    {profile.hostnames.map((host, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-2 py-0.5 bg-white border border-zinc-200 rounded text-xs font-mono text-zinc-700"
                      >
                        {host}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Remaining specialties (skip first, already shown above) */}
            {allSpecialties.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {allSpecialties.slice(1).map((s, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 bg-zinc-100 rounded-full text-xs text-zinc-600">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Timeline (monitor-style vertical timeline) ───────────────────────

const TIMELINE_CATEGORY_CONFIG = {
  'blog_post': { icon: 'ph-article', dotColor: 'bg-green-500', badgeBg: 'bg-green-100', badgeText: 'text-green-700', label: 'blog' },
  'news': { icon: 'ph-newspaper', dotColor: 'bg-sky-500', badgeBg: 'bg-sky-100', badgeText: 'text-sky-700', label: 'news' },
  'website_change': { icon: 'ph-globe', dotColor: 'bg-amber-500', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700', label: 'website' },
  'x_post': { icon: 'ph-x-logo', dotColor: 'bg-zinc-700', badgeBg: 'bg-zinc-100', badgeText: 'text-zinc-700', label: 'x post' },
};

const TIMELINE_PREVIEW_ITEMS = [
  { category: 'blog_post', time: '2 hours ago', title: 'New product launch announcement' },
  { category: 'x_post', time: '5 hours ago', title: 'Industry partnership revealed' },
  { category: 'website_change', time: 'Yesterday', title: 'Pricing page updated with new tiers' },
  { category: 'news', time: '2 days ago', title: 'Featured in major tech publication' },
];

// Static (non-animated) faux-timeline rows used as a preview behind empty-state
// CTAs so users can see what tracked updates look like.
const TimelinePreviewRows = ({ items = TIMELINE_PREVIEW_ITEMS, opacity = 'opacity-60' }) => (
  <div className={`space-y-2 select-none pointer-events-none ${opacity}`}>
    {items.map((item, idx) => {
      const cat = TIMELINE_CATEGORY_CONFIG[item.category] || TIMELINE_CATEGORY_CONFIG['news'];
      return (
        <div key={idx} className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cat.dotColor}`}></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${cat.badgeBg} ${cat.badgeText}`}>
                  {cat.label}
                </span>
                <span className="text-[10px] text-zinc-400">{item.time}</span>
              </div>
              <p className="text-sm text-zinc-700 truncate">{item.title}</p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const TimelineItem = ({ item, isLast }) => {
  const cat = TIMELINE_CATEGORY_CONFIG[item.category] || TIMELINE_CATEGORY_CONFIG['news'];
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    const el = descRef.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [item.description]);

  return (
    <div className={`relative flex items-start gap-4 pl-1 ${isLast ? '' : 'mb-6'}`}>
      {/* Dot */}
      <div className="w-8 flex items-center justify-center relative z-10 mt-1">
        <div className={`w-2.5 h-2.5 rounded-full ${cat.dotColor} ring-4 ring-white`}></div>
      </div>
      {/* Card */}
      <div className="flex-1 bg-white rounded-xl border border-zinc-200 shadow-sm p-4 hover:shadow-md hover:border-zinc-300 transition-all">
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cat.badgeBg} ${cat.badgeText}`}>{cat.label}</span>
          <span className="text-xs text-zinc-400 ml-auto">{timeAgo(item.pub_date)}</span>
        </div>
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-zinc-800 hover:text-purple-600 transition-colors"
        >
          {item.title}
        </a>
        {item.description && (
          <div>
            <p ref={descRef} className={`text-sm text-zinc-600 mt-1 whitespace-pre-line ${expanded ? '' : 'line-clamp-2'}`}>
              {item.description}
            </p>
            {(clamped || expanded) && (
              <button
                onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                className="text-xs text-purple-600 hover:text-purple-700 mt-0.5 font-medium"
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TimelineDateLabel = ({ label, isToday }) => (
  <div className="relative flex items-center gap-4 mb-6">
    <div className="w-10 h-6 flex items-center justify-center relative z-10">
      <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 border ${
        isToday ? 'text-purple-600 bg-purple-50 border-purple-200' : 'text-zinc-500 bg-zinc-100 border-zinc-200'
      }`}>{label}</span>
    </div>
  </div>
);

// ─── Data Sections ────────────────────────────────────────────────────

const CustomersSection = ({ slug, domain, companyName, isFreeCompany, initialProspectorState }) => {
  const [expanded, setExpanded] = useState(true);
  const [showCurl, setShowCurl] = useState(false);
  const [previewCustomers, setPreviewCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleLimit, setVisibleLimit] = useState(PROFILE_CARD_INITIAL_LIMIT);
  const prospectorJob = useProfileProspectorJob(initialProspectorState);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    ExplorerApi.getPreviewCustomers(slug)
      .then(data => { setPreviewCustomers(data.customers || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, domain]);

  useEffect(() => {
    setVisibleLimit(PROFILE_CARD_INITIAL_LIMIT);
  }, [slug, domain, initialProspectorState?.sheet?.id]);

  const [viewAllLoading, setViewAllLoading] = useState(false);
  const visibleCustomers = previewCustomers;
  const prospectorCustomers = sheetRowsToCustomers(prospectorJob.rows);
  const hasProspectorResults = prospectorCustomers.length > 0;
  const prospectorHref = prospectorJob.jobState?.redirect_url;
  const prospectorMode = prospectorJob.jobState?.mode;
  const cardLoading = prospectorJob.active || prospectorJob.queueing;
  const cappedVisibleLimit = Math.min(visibleLimit, PROFILE_CARD_EXPANDED_LIMIT);
  const visibleProspectorCustomers = prospectorCustomers.slice(0, cappedVisibleLimit);
  const canShowMore = (
    hasProspectorResults
    && visibleProspectorCustomers.length < prospectorCustomers.length
    && visibleProspectorCustomers.length < PROFILE_CARD_EXPANDED_LIMIT
  );

  const openCustomersInProspector = (always) => {
    prospectorJob.start(
      () => ExplorerApi.openCompanyProfileProspector('customers', domain, 'preview'),
      { storageKey: 'explorer_skip_credit_modal_customers', always },
    );
  };

  const viewAllInProspector = async () => {
    if (viewAllLoading) return;
    if (prospectorMode === 'full' && prospectorHref) {
      window.location.href = prospectorHref;
      return;
    }
    setViewAllLoading(true);
    try {
      const data = await ExplorerApi.openCompanyProfileProspector('customers', domain, 'full');
      if (data?.success) {
        const target = data.redirect_url || prospectorHref;
        if (target) window.location.href = target;
      }
    } finally {
      setViewAllLoading(false);
    }
  };

  const dashboardOverlay = (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-white via-white/80 to-transparent">
      {localStorage.getItem('explorer_skip_credit_modal_customers') === 'true' ? (
        <DashboardRevealButton
          label="Explore Customer Data"
          creditText={PRICING_DESCRIPTIONS.customers.short}
          onReveal={() => openCustomersInProspector(false)}
          loading={prospectorJob.queueing}
        />
      ) : (
        <DashboardCTADropdown
          label="Explore Customer Data"
          creditCost={1}
          creditDescription={PRICING_DESCRIPTIONS.customers.full}
          onReveal={openCustomersInProspector}
          loading={prospectorJob.queueing}
        />
      )}
    </div>
  );

  return (
    <div className="relative">
      <div className={cardLoading ? 'blur-[2px] opacity-60 pointer-events-none select-none' : ''}>
      <DataSectionHeader
        icon="users-three"
        title="Customers"
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        actions={
          <button
            onClick={() => setShowCurl(!showCurl)}
            className="text-xs text-zinc-400 hover:text-purple-600 transition-colors font-mono"
            title="Show API command"
          >
            &lt;/&gt;
          </button>
        }
      />
      {expanded && (
        <div className="mt-4">
          {showCurl && (
            <div className="mb-4">
              <CurlCommandBox endpoint="/api/v1/customer/listing" domain={domain} />
            </div>
          )}
          {loading ? (
            <SkeletonCardBody rows={5} variant="avatarText" />
          ) : isCreditError(prospectorJob.error) ? (
            <ErrorPanel
              error={prospectorJob.error}
              onRetry={() => openCustomersInProspector(false)}
              retrying={prospectorJob.queueing}
            />
          ) : (
            <>
              {prospectorJob.error && (
                <div className="mb-4">
                  <ErrorPanel
                    error={prospectorJob.error}
                    onRetry={() => openCustomersInProspector(false)}
                    retrying={prospectorJob.queueing}
                  />
                </div>
              )}
              {hasProspectorResults ? (
                <>
                  <CustomerMiniTable customers={visibleProspectorCustomers} />
                  <ProfileCardResultsFooter
                    shown={visibleProspectorCustomers.length}
                    total={prospectorCustomers.length}
                    label="customers"
                    href={prospectorHref}
                    canShowMore={canShowMore}
                    onShowMore={() => setVisibleLimit(PROFILE_CARD_EXPANDED_LIMIT)}
                    onViewAll={prospectorMode === 'preview' ? viewAllInProspector : undefined}
                    viewAllLoading={viewAllLoading}
                  />
                </>
              ) : (
                visibleCustomers.length > 0 && (
                  <CustomerMiniTable customers={visibleCustomers} />
                )
              )}
              {!hasProspectorResults && (
                prospectorJob.completed ? (
                  <div className={`${visibleCustomers.length > 0 ? 'mt-4' : ''} rounded-xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50/60 px-4 py-6 text-center`}>
                    <i className="ph ph-users-three text-3xl text-zinc-300 mb-2 block"></i>
                    <p className="text-sm font-medium text-zinc-700 mb-1">No customers found</p>
                    <p className="text-xs text-zinc-500 mb-3">We couldn't surface any customer signals for {companyName}.</p>
                    <ProspectorQueuedFooter href={prospectorHref} label="View all in Prospector" />
                  </div>
                ) : (
                  <div className={`relative ${visibleCustomers.length > 0 ? 'mt-3' : ''}`}>
                    <CustomerMiniTable customers={BLURRED_CUSTOMER_PLACEHOLDERS} blurred={true} />
                    {IS_DASHBOARD ? (
                      dashboardOverlay
                    ) : !IS_GOOGLEBOT ? (
                      <BlurredOverlay type="customers" companyName={companyName} />
                    ) : null}
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
      </div>
      {cardLoading && (
        <ProfileCardLoadingOverlay
          title="Loading customers"
          href={prospectorHref}
          progress={prospectorJob.progress}
          elapsedSeconds={prospectorJob.elapsedSeconds}
        />
      )}
    </div>
  );
};

const CompetitorsSection = ({ slug, domain, companyName, isFreeCompany, initialProspectorState }) => {
  const [expanded, setExpanded] = useState(true);
  const [showCurl, setShowCurl] = useState(false);
  const [previewCompetitors, setPreviewCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleLimit, setVisibleLimit] = useState(PROFILE_CARD_INITIAL_LIMIT);
  const prospectorJob = useProfileProspectorJob(initialProspectorState);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    ExplorerApi.getPreviewCompetitors(slug)
      .then(data => { setPreviewCompetitors(data.competitors || []); })
      .catch(() => {})
      .finally(() => setLoading(false));

  }, [slug, domain]);

  useEffect(() => {
    setVisibleLimit(PROFILE_CARD_INITIAL_LIMIT);
  }, [slug, domain, initialProspectorState?.sheet?.id]);

  const [viewAllLoading, setViewAllLoading] = useState(false);
  const visibleCompetitors = previewCompetitors;
  const prospectorCompetitors = sheetRowsToCompetitors(prospectorJob.rows);
  const hasProspectorResults = prospectorCompetitors.length > 0;
  const prospectorHref = prospectorJob.jobState?.redirect_url;
  const prospectorMode = prospectorJob.jobState?.mode;
  const cardLoading = prospectorJob.active || prospectorJob.queueing;
  const cappedVisibleLimit = Math.min(visibleLimit, PROFILE_CARD_EXPANDED_LIMIT);
  const visibleProspectorCompetitors = prospectorCompetitors.slice(0, cappedVisibleLimit);
  const canShowMore = (
    hasProspectorResults
    && visibleProspectorCompetitors.length < prospectorCompetitors.length
    && visibleProspectorCompetitors.length < PROFILE_CARD_EXPANDED_LIMIT
  );

  const openCompetitorsInProspector = (always) => {
    prospectorJob.start(
      () => ExplorerApi.openCompanyProfileProspector('competitors', domain, 'preview'),
      { storageKey: 'explorer_skip_credit_modal_competitors', always },
    );
  };

  const viewAllInProspector = async () => {
    if (viewAllLoading) return;
    if (prospectorMode === 'full' && prospectorHref) {
      window.location.href = prospectorHref;
      return;
    }
    setViewAllLoading(true);
    try {
      const data = await ExplorerApi.openCompanyProfileProspector('competitors', domain, 'full');
      if (data?.success) {
        const target = data.redirect_url || prospectorHref;
        if (target) window.location.href = target;
      }
    } finally {
      setViewAllLoading(false);
    }
  };

  const dashboardOverlay = (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-white via-white/80 to-transparent">
      {localStorage.getItem('explorer_skip_credit_modal_competitors') === 'true' ? (
        <DashboardRevealButton
          label="Explore Competitor Data"
          creditText={PRICING_DESCRIPTIONS.competitors.short}
          onReveal={() => openCompetitorsInProspector(false)}
          loading={prospectorJob.queueing}
        />
      ) : (
        <DashboardCTADropdown
          label="Explore Competitor Data"
          creditCost={5}
          creditDescription={PRICING_DESCRIPTIONS.competitors.full}
          onReveal={openCompetitorsInProspector}
          loading={prospectorJob.queueing}
        />
      )}
    </div>
  );

  return (
    <div className="relative">
      <div className={cardLoading ? 'blur-[2px] opacity-60 pointer-events-none select-none' : ''}>
      <DataSectionHeader
        icon="sword"
        title="Competitors"
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        actions={
          <button
            onClick={() => setShowCurl(!showCurl)}
            className="text-xs text-zinc-400 hover:text-purple-600 transition-colors font-mono"
            title="Show API command"
          >
            &lt;/&gt;
          </button>
        }
      />
      {expanded && (
        <div className="mt-4">
          {showCurl && (
            <div className="mb-4">
              <CurlCommandBox endpoint="/api/v1/competitor/listing" domain={domain} />
            </div>
          )}
          {loading ? (
            <SkeletonCardBody rows={5} variant="avatarText" />
          ) : isCreditError(prospectorJob.error) ? (
            <ErrorPanel
              error={prospectorJob.error}
              onRetry={() => openCompetitorsInProspector(false)}
              retrying={prospectorJob.queueing}
            />
          ) : (
            <>
              {prospectorJob.error && (
                <div className="mb-4">
                  <ErrorPanel
                    error={prospectorJob.error}
                    onRetry={() => openCompetitorsInProspector(false)}
                    retrying={prospectorJob.queueing}
                  />
                </div>
              )}
              {hasProspectorResults ? (
                <>
                  <CompetitorMiniTable competitors={visibleProspectorCompetitors} />
                  <ProfileCardResultsFooter
                    shown={visibleProspectorCompetitors.length}
                    total={prospectorCompetitors.length}
                    label="competitors"
                    href={prospectorHref}
                    canShowMore={canShowMore}
                    onShowMore={() => setVisibleLimit(PROFILE_CARD_EXPANDED_LIMIT)}
                    onViewAll={prospectorMode === 'preview' ? viewAllInProspector : undefined}
                    viewAllLoading={viewAllLoading}
                  />
                </>
              ) : (
                visibleCompetitors.length > 0 && (
                  <CompetitorMiniTable competitors={visibleCompetitors} />
                )
              )}
              {!hasProspectorResults && (
                prospectorJob.completed ? (
                  <div className={`${visibleCompetitors.length > 0 ? 'mt-4' : ''} rounded-xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50/60 px-4 py-6 text-center`}>
                    <i className="ph ph-target text-3xl text-zinc-300 mb-2 block"></i>
                    <p className="text-sm font-medium text-zinc-700 mb-1">No competitors found</p>
                    <p className="text-xs text-zinc-500 mb-3">We couldn't surface any competitive signals for {companyName}.</p>
                    <ProspectorQueuedFooter href={prospectorHref} label="View all in Prospector" />
                  </div>
                ) : (
                  <div className={`relative ${visibleCompetitors.length > 0 ? 'mt-3' : ''}`}>
                    <CompetitorMiniTable competitors={BLURRED_COMPETITOR_PLACEHOLDERS} blurred={true} />
                    {IS_DASHBOARD ? (
                      dashboardOverlay
                    ) : !IS_GOOGLEBOT ? (
                      <BlurredOverlay type="competitors" companyName={companyName} />
                    ) : null}
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
      </div>
      {cardLoading && (
        <ProfileCardLoadingOverlay
          title="Loading competitors"
          href={prospectorHref}
          progress={prospectorJob.progress}
          elapsedSeconds={prospectorJob.elapsedSeconds}
        />
      )}
    </div>
  );
};

const FundingSection = ({ slug, domain, initialProspectorState }) => {
  const [expanded, setExpanded] = useState(true);
  const [showCurl, setShowCurl] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(PROFILE_CARD_INITIAL_LIMIT);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewTotalRaised, setPreviewTotalRaised] = useState(null);
  const prospectorJob = useProfileProspectorJob(initialProspectorState);

  useEffect(() => {
    setVisibleLimit(PROFILE_CARD_INITIAL_LIMIT);
  }, [domain, initialProspectorState?.sheet?.id]);

  useEffect(() => {
    if (!slug) return;
    ExplorerApi.getPreviewFunding(slug)
      .then(data => {
        if (data?.success) {
          setPreviewRows(data.rows || []);
          setPreviewTotalRaised(data.total_funds_raised_usd ?? null);
        }
      })
      .catch(() => {});
  }, [slug]);

  const [viewAllLoading, setViewAllLoading] = useState(false);
  const hasProspectorRows = (prospectorJob.rows || []).length > 0;
  const sourceRows = hasProspectorRows ? prospectorJob.rows : previewRows;
  const rounds = sheetRowsToFundingRounds(sourceRows);
  const totalRaisedValue = (prospectorJob.rows || []).find(row => row.total_funds_raised_usd)?.total_funds_raised_usd
    ?? previewTotalRaised;
  const totalRaised = formatCurrency(totalRaisedValue) || 'Unknown';
  const lastRound = rounds[0] ? `${rounds[0].round} · ${rounds[0].date}` : 'None found';
  const prospectorHref = prospectorJob.jobState?.redirect_url;
  const prospectorMode = prospectorJob.jobState?.mode;
  const cardLoading = prospectorJob.active || prospectorJob.queueing;
  const cappedVisibleLimit = Math.min(visibleLimit, PROFILE_CARD_EXPANDED_LIMIT);
  const visibleRounds = rounds.slice(0, cappedVisibleLimit);
  const canShowMore = (
    visibleRounds.length < rounds.length
    && visibleRounds.length < PROFILE_CARD_EXPANDED_LIMIT
  );

  const loadFunding = (always) => {
    if (!domain) return;
    prospectorJob.start(
      () => ExplorerApi.openCompanyProfileProspector('funding', domain, 'preview'),
      { storageKey: 'explorer_skip_credit_modal_funding', always },
    );
  };

  const viewAllInProspector = async () => {
    if (viewAllLoading || !domain) return;
    if (prospectorMode === 'full' && prospectorHref) {
      window.location.href = prospectorHref;
      return;
    }
    setViewAllLoading(true);
    try {
      const data = await ExplorerApi.openCompanyProfileProspector('funding', domain, 'full');
      if (data?.success) {
        const target = data.redirect_url || prospectorHref;
        if (target) window.location.href = target;
      }
    } finally {
      setViewAllLoading(false);
    }
  };

  const showFooterViewAll = !hasProspectorRows || prospectorMode === 'preview';

  const dashboardOverlay = (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-white via-white/80 to-transparent">
      {localStorage.getItem('explorer_skip_credit_modal_funding') === 'true' ? (
        <DashboardRevealButton
          label="Reveal Funding Data"
          creditText={PRICING_DESCRIPTIONS.funding.short}
          onReveal={() => loadFunding(false)}
          loading={prospectorJob.queueing}
        />
      ) : (
        <DashboardCTADropdown
          label="Reveal Funding Data"
          creditCost={2}
          creditDescription={PRICING_DESCRIPTIONS.funding.full}
          onReveal={loadFunding}
          loading={prospectorJob.queueing}
        />
      )}
    </div>
  );

  return (
    <div className="relative">
      <div className={cardLoading ? 'blur-[2px] opacity-60 pointer-events-none select-none' : ''}>
      <DataSectionHeader
        icon="currency-circle-dollar"
        title="Funding"
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCurl(!showCurl)}
              className="text-xs text-zinc-400 hover:text-purple-600 transition-colors font-mono"
              title="Show API command"
            >
              &lt;/&gt;
            </button>
          </div>
        }
      />
      {expanded && (
        <div className="mt-4">
          {showCurl && (
            <div className="mb-4">
              <CurlCommandBox endpoint="/api/v1/company/funding" domain={domain} />
            </div>
          )}
          {isCreditError(prospectorJob.error) ? (
            <ErrorPanel
              error={prospectorJob.error}
              onRetry={() => loadFunding(false)}
              retrying={prospectorJob.queueing}
            />
          ) : (
          <>
          {prospectorJob.error && (
            <div className="mb-4">
              <ErrorPanel
                error={prospectorJob.error}
                onRetry={() => loadFunding(false)}
                retrying={prospectorJob.queueing}
              />
            </div>
          )}
          {rounds.length > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Total raised</div>
                  <div className="text-sm font-semibold text-zinc-900 tabular-nums">{totalRaised}</div>
                </div>
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Rounds</div>
                  <div className="text-sm font-semibold text-zinc-900 tabular-nums">{rounds.length}</div>
                </div>
                <div className="bg-zinc-50 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Last round</div>
                  <div className="text-sm font-medium text-zinc-700">{lastRound}</div>
                </div>
              </div>
              <FundingMiniTable rounds={visibleRounds} />
              <ProfileCardResultsFooter
                shown={visibleRounds.length}
                total={rounds.length}
                label="funding rounds"
                href={prospectorHref}
                canShowMore={canShowMore}
                onShowMore={() => setVisibleLimit(PROFILE_CARD_EXPANDED_LIMIT)}
                onViewAll={showFooterViewAll ? viewAllInProspector : undefined}
                viewAllLoading={viewAllLoading}
              />
            </>
          ) : prospectorJob.completed ? (
            <div className="rounded-xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50/60 px-4 py-6 text-center">
              <i className="ph ph-currency-circle-dollar text-3xl text-zinc-300 mb-2 block"></i>
              <p className="text-sm font-medium text-zinc-700 mb-1">No funding rounds found</p>
              <p className="text-xs text-zinc-500 mb-3">We couldn't surface any funding history for {domain}.</p>
              <ProspectorQueuedFooter href={prospectorHref} label="View all in Prospector" />
            </div>
          ) : (
            <div className="relative">
              <FundingMiniTable rounds={BLURRED_FUNDING_PLACEHOLDERS} blurred={true} />
              {IS_DASHBOARD ? (
                dashboardOverlay
              ) : !IS_GOOGLEBOT ? (
                <BlurredOverlay type="funding rounds" companyName={domain} />
              ) : null}
            </div>
          )}
          </>
          )}
        </div>
      )}
      </div>
      {cardLoading && (
        <ProfileCardLoadingOverlay
          title="Loading funding"
          href={prospectorHref}
          progress={prospectorJob.progress}
          elapsedSeconds={prospectorJob.elapsedSeconds}
        />
      )}
    </div>
  );
};

const TimelineSection = ({ slug, domain, companyName, isFreeCompany, initialProspectorState }) => {
  const [expanded, setExpanded] = useState(true);
  const [showFeedModal, setShowFeedModal] = useState(false);
  const [timelineItems, setTimelineItems] = useState([]);
  const [hasFeed, setHasFeed] = useState(false);
  const [hasOwnFeed, setHasOwnFeed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visibleLimit, setVisibleLimit] = useState(PROFILE_CARD_INITIAL_LIMIT);
  const updatesJob = useProfileProspectorJob(initialProspectorState);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    const mode = IS_DASHBOARD ? 'dashboard' : 'public';
    ExplorerApi.getTimeline(slug, mode)
      .then(data => {
        setTimelineItems(data.items || []);
        setHasFeed(data.has_feed || false);
        setHasOwnFeed(data.has_own_feed || false);
        setHasMore(data.has_more || false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    setVisibleLimit(PROFILE_CARD_INITIAL_LIMIT);
  }, [slug, domain, initialProspectorState?.sheet?.id]);

  const [viewAllLoading, setViewAllLoading] = useState(false);
  // Separate full items from censored stubs
  const fullItems = timelineItems.filter(item => !item.censored);
  const hasCensored = timelineItems.some(item => item.censored);
  const prospectorTimelineItems = sheetRowsToTimelineItems(updatesJob.rows);
  const hasProspectorResults = prospectorTimelineItems.length > 0;
  const prospectorHref = updatesJob.jobState?.redirect_url;
  const prospectorMode = updatesJob.jobState?.mode;
  const cardLoading = updatesJob.active || updatesJob.queueing;
  const cappedVisibleLimit = Math.min(visibleLimit, PROFILE_CARD_EXPANDED_LIMIT);
  const visibleTimelineItems = hasProspectorResults
    ? prospectorTimelineItems.slice(0, cappedVisibleLimit)
    : fullItems;
  const canShowMore = (
    hasProspectorResults
    && visibleTimelineItems.length < prospectorTimelineItems.length
    && visibleTimelineItems.length < PROFILE_CARD_EXPANDED_LIMIT
  );

  // Group visible (full) items by date
  const groups = [];
  let currentLabel = null;
  visibleTimelineItems.forEach(item => {
    const label = getDateLabel(item.pub_date);
    if (label !== currentLabel) {
      groups.push({ type: 'label', label, isToday: label === 'Today' });
      currentLabel = label;
    }
    groups.push({ type: 'item', item });
  });

  const handleSubscribe = () => {
    window.trackFeature?.('explorer-timeline');
    if (IS_DASHBOARD && window.NewFeedModal) {
      setShowFeedModal(true);
    } else {
      window.location.href = `/dashboard/monitor#new-feed?domain=${encodeURIComponent(domain)}`;
    }
  };

  const fetchUpdatesInProspector = (always) => {
    updatesJob.start(
      () => ExplorerApi.openCompanyProfileProspector('updates', domain, 'preview'),
      { storageKey: 'explorer_skip_credit_modal_updates', always },
    );
  };

  const viewAllInProspector = async () => {
    if (viewAllLoading || !domain) return;
    if (prospectorMode === 'full' && prospectorHref) {
      window.location.href = prospectorHref;
      return;
    }
    setViewAllLoading(true);
    try {
      const data = await ExplorerApi.openCompanyProfileProspector('updates', domain, 'full');
      if (data?.success) {
        const target = data.redirect_url || prospectorHref;
        if (target) window.location.href = target;
      }
    } finally {
      setViewAllLoading(false);
    }
  };

  const handleCreateFeed = async (feedData) => {
    try {
      if (window.feedsApi) {
        await window.feedsApi('', {
          method: 'POST',
          body: feedData,
        });
        // Refresh timeline after feed creation
        const mode = IS_DASHBOARD ? 'dashboard' : 'public';
        const data = await ExplorerApi.getTimeline(slug, mode);
        setTimelineItems(data.items || []);
        setHasFeed(data.has_feed || false);
        setHasOwnFeed(data.has_own_feed || false);
        setHasMore(data.has_more || false);
      }
      setShowFeedModal(false);
    } catch (err) {
      console.error('Error creating feed:', err);
      throw err;
    }
  };

  const NewFeedModalComponent = window.NewFeedModal;

  return (
    <div className="relative">
      <div className={cardLoading ? 'blur-[2px] opacity-60 pointer-events-none select-none' : ''}>
      <DataSectionHeader
        icon="clock-counter-clockwise"
        title="Business Update Timeline"
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      />
      {expanded && (
        <div className="mt-4">
          {isCreditError(updatesJob.error) ? (
            <ErrorPanel
              error={updatesJob.error}
              onRetry={() => fetchUpdatesInProspector(false)}
              retrying={updatesJob.queueing}
            />
          ) : (<>
          {updatesJob.error && (
            <div className="mb-4">
              <ErrorPanel
                error={updatesJob.error}
                onRetry={() => fetchUpdatesInProspector(false)}
                retrying={updatesJob.queueing}
              />
            </div>
          )}
          {loading ? (
            <SkeletonCardBody rows={4} variant="timelineItem" />
          ) : visibleTimelineItems.length > 0 ? (
            <>
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-zinc-200"></div>
                {groups.map((entry, idx) => {
                  if (entry.type === 'label') {
                    return <TimelineDateLabel key={`label-${idx}`} label={entry.label} isToday={entry.isToday} />;
                  }
                  const isLast = idx === groups.length - 1 && (!hasCensored || hasProspectorResults);
                  return <TimelineItem key={idx} item={entry.item} isLast={isLast} />;
                })}
              </div>
              {hasProspectorResults && (
                <ProfileCardResultsFooter
                  shown={visibleTimelineItems.length}
                  total={prospectorTimelineItems.length}
                  label="updates"
                  href={prospectorHref}
                  canShowMore={canShowMore}
                  onShowMore={() => setVisibleLimit(PROFILE_CARD_EXPANDED_LIMIT)}
                  onViewAll={prospectorMode === 'preview' ? viewAllInProspector : undefined}
                  viewAllLoading={viewAllLoading}
                />
              )}
              {IS_DASHBOARD && (
                <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-zinc-400">
                    {hasProspectorResults ? 'Saved updates' : 'Cached updates'}
                  </span>
                  <div className="flex items-center gap-2">
                    {localStorage.getItem('explorer_skip_credit_modal_updates') === 'true' ? (
                      <button
                        onClick={() => fetchUpdatesInProspector(false)}
                        disabled={updatesJob.queueing}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                      >
                        <i className={`ph ${updatesJob.queueing ? 'ph-spinner animate-spin' : 'ph-arrow-clockwise'} text-sm`}></i>
                        Refresh
                        <span className="text-purple-400 text-xs">({PRICING_DESCRIPTIONS.updates.short})</span>
                      </button>
                    ) : (
                      <DashboardCTADropdown
                        label="Refresh"
                        creditCost={2}
                        creditDescription={PRICING_DESCRIPTIONS.updates.full}
                        onReveal={fetchUpdatesInProspector}
                        loading={updatesJob.queueing}
                      />
                    )}
                    {!hasOwnFeed && (
                      <button
                        onClick={handleSubscribe}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                      >
                        <i className="ph ph-rss text-sm"></i>
                        Subscribe
                      </button>
                    )}
                  </div>
                </div>
              )}
              {!IS_DASHBOARD && isFreeCompany && (
                <div className="text-center mt-4 py-3 bg-purple-50 border border-purple-100 rounded-xl">
                  <p className="text-sm text-zinc-600 mb-2">Subscribe to follow {companyName}'s updates.</p>
                  <a href="/auth/register" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors">
                    <i className="ph ph-rss text-sm"></i>
                    Sign up to monitor this company
                  </a>
                </div>
              )}
            </>
          ) : !IS_DASHBOARD && timelineItems.length === 0 && isFreeCompany ? (
            <div className="relative rounded-xl bg-zinc-50 p-3">
              <TimelinePreviewRows items={TIMELINE_PREVIEW_ITEMS.slice(0, 3)} />
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-gradient-to-t from-zinc-50 via-zinc-50/90 to-zinc-50/40">
                <div className="text-center p-4">
                  <p className="text-sm font-medium text-zinc-700 mb-1">No updates tracked for {companyName} yet</p>
                  <p className="text-xs text-zinc-500 mb-3">Sign up to follow blog posts, website changes, news and more.</p>
                  <a href="/auth/register" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors">
                    <i className="ph ph-rss text-sm"></i>
                    Sign up to subscribe
                  </a>
                </div>
              </div>
            </div>
          ) : !IS_DASHBOARD && timelineItems.length === 0 ? (
            <div className="relative rounded-xl bg-zinc-50 p-3">
              <TimelinePreviewRows />
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-gradient-to-t from-zinc-50 via-zinc-50/90 to-zinc-50/40">
                <div className="text-center p-4">
                  <p className="text-sm text-zinc-700 font-medium mb-1">Track {companyName}'s updates</p>
                  <p className="text-xs text-zinc-500 mb-3">Get notified about blog posts, news, and more.</p>
                  <a
                    href="/auth/register"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <i className="ph ph-user-plus text-sm"></i>
                    Sign up to monitor this company
                  </a>
                </div>
              </div>
            </div>
          ) : IS_DASHBOARD ? (
            updatesJob.completed ? (
              <div className="relative rounded-xl bg-zinc-50 p-3">
                <TimelinePreviewRows items={TIMELINE_PREVIEW_ITEMS.slice(0, 3)} opacity="opacity-40" />
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-gradient-to-t from-zinc-50 via-zinc-50/95 to-zinc-50/60">
                  <div className="text-center px-6 py-4 max-w-sm">
                    <p className="text-sm font-medium text-zinc-700 mb-1">No updates found for {companyName}</p>
                    <p className="text-xs text-zinc-500 mb-3">We didn't pick up any tracked changes for this company.</p>
                    <ProspectorQueuedFooter href={prospectorHref} label="View all in Prospector" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl bg-zinc-50 p-3">
                <TimelinePreviewRows opacity="opacity-40" />
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-gradient-to-t from-zinc-50 via-zinc-50/95 to-zinc-50/60">
                  <div className="text-center px-6 py-4 max-w-md">
                    <p className="text-sm font-medium text-zinc-700 mb-1">No updates indexed for {companyName} yet</p>
                    <p className="text-xs text-zinc-500 mb-4">
                      {hasOwnFeed
                        ? "You're already monitoring this company. Pull a fresh snapshot any time."
                        : "Pull a one-time snapshot, or subscribe for ongoing monitoring."}
                    </p>
                    <div className="inline-flex items-center gap-2 flex-wrap justify-center">
                      {localStorage.getItem('explorer_skip_credit_modal_updates') === 'true' ? (
                        <button
                          onClick={() => fetchUpdatesInProspector(false)}
                          disabled={updatesJob.queueing}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60"
                        >
                          <i className={`ph ${updatesJob.queueing ? 'ph-spinner animate-spin' : 'ph-table'} text-sm`}></i>
                          Fetch Updates
                          <span className="text-purple-200 text-xs">({PRICING_DESCRIPTIONS.updates.short})</span>
                        </button>
                      ) : (
                        <PrimaryCTADropdown
                          label="Fetch Updates"
                          icon="ph-table"
                          creditCost={2}
                          creditDescription={PRICING_DESCRIPTIONS.updates.full}
                          onReveal={fetchUpdatesInProspector}
                          loading={updatesJob.queueing}
                        />
                      )}
                      {!hasOwnFeed && (
                        <button
                          onClick={handleSubscribe}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-purple-600 bg-white border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                        >
                          <i className="ph ph-rss text-sm"></i>
                          Subscribe
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : null}

          {/* Blurred area with CTA for censored items */}
          {hasCensored && !hasProspectorResults && !updatesJob.active && !updatesJob.queueing && !updatesJob.completed && (
            <div className="relative mt-3">
              <div className="blur-sm select-none pointer-events-none space-y-3">
                {timelineItems.filter(i => i.censored).slice(0, 3).map((_, idx) => (
                  <div key={idx} className="bg-white border border-zinc-200 rounded-xl p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-zinc-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-zinc-200 rounded w-48 mb-2"></div>
                        <div className="h-3 bg-zinc-100 rounded w-64"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-zinc-50 via-zinc-50/90 to-transparent">
                <div className="text-center p-4">
                  {IS_DASHBOARD ? (
                    localStorage.getItem('explorer_skip_credit_modal_updates') === 'true' ? (
                      <button
                        onClick={() => fetchUpdatesInProspector(false)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <i className="ph ph-table text-sm"></i>
                        Fetch Updates
                        <span className="text-purple-200 text-xs">({PRICING_DESCRIPTIONS.updates.short})</span>
                      </button>
                    ) : (
                      <PrimaryCTADropdown
                        label="Fetch Updates"
                        icon="ph-table"
                        creditCost={2}
                        creditDescription={PRICING_DESCRIPTIONS.updates.full}
                        onReveal={fetchUpdatesInProspector}
                        loading={updatesJob.queueing}
                      />
                    )
                  ) : (
                    <a
                      href="/auth/register"
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <i className="ph ph-user-plus text-sm"></i>
                      Sign up to see all updates
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
          </>)}
        </div>
      )}
      </div>
      {cardLoading && (
        <ProfileCardLoadingOverlay
          title="Loading updates"
          href={prospectorHref}
          progress={updatesJob.progress}
          elapsedSeconds={updatesJob.elapsedSeconds}
        />
      )}

      {/* NewFeedModal inline */}
      {showFeedModal && NewFeedModalComponent && (
        <NewFeedModalComponent
          onClose={() => setShowFeedModal(false)}
          onCreateFeed={handleCreateFeed}
          initialTargets={[{ url: domain, blog: true, x: true, website: true, freq: 7 }]}
        />
      )}
    </div>
  );
};

// ─── Search Bar ─────────────────────────────────────────────────

const IQSearchBar = ({ currentDomain }) => {
  const handleSelect = (company) => {
    window.trackFeature?.('explorer-search');
    navigateTo(company.slug || company.domain);
  };

  if (window.CompanySearchBar) {
    return (
      <CompanySearchBar
        onSelect={handleSelect}
        placeholder="Enter website URL (e.g., stripe.com)"
        size="large"
        searchApiUrl="/api/explorer/search"
      />
    );
  }

  return <FallbackSearchBar currentDomain={currentDomain} />;
};

const FallbackSearchBar = ({ currentDomain }) => {
  const [query, setQuery] = useState(currentDomain || '');
  const [error, setError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (window.DomainValidator) {
      const result = window.DomainValidator.validate(query);
      if (!result.valid) { setError(result.error); return; }
      setError(null);
      navigateTo(result.domain);
    } else {
      const domain = query.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      if (!domain || !domain.includes('.')) { setError('Please enter a valid URL or domain'); return; }
      setError(null);
      navigateTo(domain);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setError(null); }}
          placeholder="Enter a company domain (e.g. stripe.com)"
          className={`w-full px-6 py-5 pl-14 pr-24 text-lg border ${error ? 'border-red-400' : 'border-zinc-300'} rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white`}
        />
        <i className="ph ph-globe absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400 text-lg"></i>
        <button
          type="submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2.5 text-base font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
        >
          Search
        </button>
      </div>
      {error && <p className="text-sm text-red-600 mt-2"><i className="ph ph-warning-circle mr-1.5"></i>{error}</p>}
    </form>
  );
};

// ─── People Also Viewed ──────────────────────────────────────────────

const PeopleAlsoViewed = ({ currentSlug }) => {
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (!currentSlug) return;
    ExplorerApi.getAlsoViewed(currentSlug)
      .then(data => setCompanies(data.companies || []))
      .catch(() => {});
  }, [currentSlug]);

  const linkBase = IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/';

  if (companies.length === 0) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4">
      <h3 className="text-sm font-semibold text-zinc-900 mb-3">People also viewed</h3>
      <div className="space-y-3">
        {companies.map(company => (
          <a
            key={company.slug || company.domain}
            href={`${linkBase}${encodeURIComponent(company.slug || company.domain)}`}
            className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-zinc-50 transition-colors"
          >
            {window.CompanyAvatar ? (
              <CompanyAvatar name={company.name} domain={company.domain} size="sm" />
            ) : (
              <div className="w-8 h-8 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-xs">
                {(company.name || company.domain || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm text-zinc-900 truncate">{company.name}</div>
              <div className="text-xs text-zinc-400 truncate">{company.domain}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

// ─── Person Profile (Mock UI) ─────────────────────────────────────────

// Format YYYY-MM strings from the API. Current roles are explicit via is_current.
const PERSON_DATE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatYearMonth = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})$/);
  if (!m) return s;
  return `${PERSON_DATE_MONTHS[parseInt(m[2], 10) - 1] || ''} ${m[1]}`.trim();
};
const formatDateRange = (start, end, isCurrent = false) => {
  const left = formatYearMonth(start);
  const right = formatYearMonth(end);
  if (isCurrent) return left ? `${left} → Present` : 'Present';
  if (left && right) return `${left} → ${right}`;
  if (left) return left;
  if (right) return `Until ${right}`;
  return '';
};
const formatLocation = (person) => {
  return person.location_display || null;
};
const formatFollowers = (n) => {
  if (n == null) return null;
  if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1) + 'K';
  return String(n);
};

const PersonAvatar = ({ name, profilePicUrl, size = 'md' }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [profilePicUrl]);
  const initials = (name || '').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
  const sizeClass = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-xl',
  }[size] || 'w-12 h-12 text-base';
  const imageUrl = failed ? null : proxiedProfilePicUrl(profilePicUrl);
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        onError={() => setFailed(true)}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div className={`${sizeClass} rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
};

const WorkEmailField = ({ person }) => {
  if (!person.work_email) return null;

  const isLookupInput = person.work_email_source === 'lookup_input';
  const [revealed, setRevealed] = useState(() =>
    isLookupInput ||
    localStorage.getItem('explorer_always_reveal_work_email') === 'true'
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isLookupInput) setRevealed(true);
  }, [isLookupInput]);

  const handleReveal = (always) => {
    if (always) localStorage.setItem('explorer_always_reveal_work_email', 'true');
    setRevealed(true);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(person.work_email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-zinc-50 rounded-lg px-3 py-2 col-span-2">
      <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1.5">
        <i className="ph ph-envelope-simple"></i>
        Work Email
      </div>
      {revealed ? (
        <div className="flex items-center gap-2">
          <a href={`mailto:${person.work_email}`} className="text-sm font-medium text-purple-600 hover:underline truncate">
            {person.work_email}
          </a>
          <button
            onClick={handleCopy}
            className="text-xs text-zinc-400 hover:text-purple-600 transition-colors flex items-center gap-1"
            title="Copy"
          >
            <i className={`ph ${copied ? 'ph-check' : 'ph-copy'} text-sm`}></i>
            {copied && <span>Copied</span>}
          </button>
        </div>
      ) : (
        <div className="relative flex items-center justify-between gap-3">
          <div className="text-sm font-medium text-zinc-700 blur-sm select-none truncate flex-1">
            ••••••@•••••••.com
          </div>
          <RevealEmailDropdown onReveal={handleReveal} />
        </div>
      )}
    </div>
  );
};

const RevealEmailDropdown = ({ onReveal }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-purple-600 bg-white border border-purple-200 rounded-md hover:bg-purple-50 transition-colors"
      >
        <i className="ph ph-eye text-xs"></i>
        Reveal
        <i className={`ph ph-caret-${open ? 'up' : 'down'} text-[10px]`}></i>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-white border border-zinc-200 rounded-xl shadow-lg z-20 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-zinc-600">
            <i className="ph ph-coins text-amber-500"></i>
            <span><strong>2 credits</strong></span>
          </div>
          <button
            onClick={() => { onReveal(true); setOpen(false); }}
            className="w-full px-3 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors text-left"
          >
            Always Reveal Work Emails
            <div className="text-xs text-purple-200 mt-0.5">I understand that it costs 2 credits every page load</div>
          </button>
          <button
            onClick={() => { onReveal(false); setOpen(false); }}
            className="w-full px-3 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
          >
            Reveal this time only
          </button>
        </div>
      )}
    </div>
  );
};

const PersonHeaderCard = ({ person, revealed = true, onReveal, revealLoading, revealError }) => {
  const [showCurl, setShowCurl] = useState(false);
  const companyHref = IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/';
  const currentRole = person.work_experience && person.work_experience.length > 0
    ? person.work_experience[0]
    : (person.current_role || null);
  const isCurrent = currentRole && currentRole.is_current === true;
  const location = formatLocation(person);
  const followers = formatFollowers(person.follower_count);
  const blurClass = 'blur-sm select-none pointer-events-none';

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6">
      <div className="flex items-start gap-4">
        <PersonAvatar name={person.full_name} profilePicUrl={person.profile_pic_url} size="xl" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="text-xl font-bold text-zinc-900">{person.full_name}</h1>
            {revealed && person.x_profile_url && (
              <a href={person.x_profile_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-zinc-900" title="X / Twitter">
                <i className="ph ph-x-logo text-sm"></i>
              </a>
            )}
            {revealed && person.personal_website && (
              <a href={person.personal_website} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-purple-600" title="Personal website">
                <i className="ph ph-globe text-sm"></i>
              </a>
            )}
            {revealed && (
              <button
                onClick={() => setShowCurl(!showCurl)}
                className="ml-auto text-xs text-zinc-400 hover:text-purple-600 transition-colors font-mono no-btn-shadow"
                title="Show API command"
              >
                &lt;/&gt;
              </button>
            )}
          </div>
          {currentRole && (
            <div className="text-sm text-zinc-700 mb-1">
              <span className="font-medium">{currentRole.role}</span>
              {revealed ? (
                currentRole.company_name && (
                  <>
                    <span className="text-zinc-400"> at </span>
                    {currentRole.company_website ? (
                      <a href={`${companyHref}${encodeURIComponent(currentRole.company_website)}`} className="text-purple-600 hover:underline">
                        {currentRole.company_name}
                      </a>
                    ) : (
                      <span className="text-zinc-700">{currentRole.company_name}</span>
                    )}
                  </>
                )
              ) : (
                <>
                  <span className="text-zinc-400"> at </span>
                  <span className={`text-zinc-700 ${blurClass}`}>Acme Corporation</span>
                </>
              )}
              {revealed && !isCurrent && currentRole.end_date && (
                <span className="text-xs text-zinc-400 ml-2">(until {formatYearMonth(currentRole.end_date)})</span>
              )}
            </div>
          )}
          {revealed
            ? (person.bio && <p className="text-sm text-zinc-600 mb-2">{person.bio}</p>)
            : (
              <p className={`text-sm text-zinc-600 mb-2 ${blurClass}`}>
                Operator turning early-stage products into category leaders. Two-time founder, pattern-matcher, and friend of the GTM crowd.
              </p>
            )}
          <div className="flex flex-wrap gap-3 text-sm text-zinc-500 mt-2">
            {revealed ? (
              <>
                {location && (
                  <span className="flex items-center gap-1">
                    <i className="ph ph-map-pin text-zinc-400"></i>
                    {location}
                  </span>
                )}
                {person.x_handle && (
                  <a
                    href={person.x_profile_url || `https://x.com/${person.x_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:text-zinc-900 transition-colors"
                  >
                    <i className="ph ph-x-logo text-zinc-400"></i>
                    @{person.x_handle}
                  </a>
                )}
                {followers && (
                  <span className="flex items-center gap-1 tabular-nums">
                    <i className="ph ph-users text-zinc-400"></i>
                    {followers} followers
                  </span>
                )}
              </>
            ) : (
              <>
                <span className={`flex items-center gap-1 ${blurClass}`}>
                  <i className="ph ph-map-pin text-zinc-400"></i>
                  San Francisco, CA
                </span>
                <span className={`flex items-center gap-1 ${blurClass}`}>
                  <i className="ph ph-x-logo text-zinc-400"></i>
                  @anonymous
                </span>
                <span className={`flex items-center gap-1 tabular-nums ${blurClass}`}>
                  <i className="ph ph-users text-zinc-400"></i>
                  12.3k followers
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {revealed && showCurl && (
        <div className="mt-4">
          <CurlCommandBox
            endpoint={PERSON_PROFILE_ENDPOINT}
            domain={person.work_email || (currentRole && currentRole.company_website) || ''}
            queryParam={person.work_email ? 'work_email' : 'employer_website'}
          />
        </div>
      )}

      {revealed ? (
        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {person.work_email && <WorkEmailField person={person} />}
            {person.personal_website && (
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Personal website</div>
                <a href={person.personal_website} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-purple-600 hover:underline truncate block">
                  {person.personal_website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
            {location && (
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Location</div>
                <div className="text-sm font-medium text-zinc-700">{location}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="relative">
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${blurClass}`}>
              <div className="bg-zinc-50 rounded-lg px-3 py-2 col-span-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Work Email</div>
                <div className="text-sm font-medium text-zinc-700">redacted@example.com</div>
              </div>
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Personal website</div>
                <div className="text-sm font-medium text-zinc-700">redacted.example.com</div>
              </div>
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Location</div>
                <div className="text-sm font-medium text-zinc-700">San Francisco, CA</div>
              </div>
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">Followers</div>
                <div className="text-sm font-medium text-zinc-700">12,300</div>
              </div>
              <div className="bg-zinc-50 rounded-lg px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1">X / Twitter</div>
                <div className="text-sm font-medium text-zinc-700">@anonymous</div>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-white via-white/95 to-white/70 rounded-lg">
              {isCreditError(revealError) ? (
                <div className="px-5 py-4 max-w-md w-full">
                  <ErrorPanel
                    error={revealError}
                    onRetry={() => onReveal && onReveal(false)}
                    retrying={revealLoading}
                  />
                </div>
              ) : revealLoading ? (
                <RevealLoadingPanel
                  title={`Revealing ${person.full_name}'s profile`}
                  subtitle="Pulling work history, education and contact details…"
                />
              ) : (
                <div className="text-center px-5 py-4 max-w-md">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 mb-3">
                    <i className="ph ph-lock-simple text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-zinc-900 mb-1">
                    Reveal {person.full_name}'s full profile
                  </h3>
                  <p className="text-xs text-zinc-600 mb-4 leading-relaxed">
                    Unlock current company, location, work email, full career history,
                    education and contact details. Pay once and keep access for everyone
                    on this workspace - future visits are free.
                  </p>
                  {revealError && (
                    <div className="mb-3">
                      <ErrorPanel
                        error={revealError}
                        onRetry={() => onReveal && onReveal(false)}
                        retrying={revealLoading}
                        compact
                      />
                    </div>
                  )}
                  <div className="flex justify-center">
                    <DashboardCTADropdown
                      label="Reveal Profile"
                      creditCost={3}
                      creditDescription={PRICING_DESCRIPTIONS.personProfile.full}
                      onReveal={onReveal}
                      loading={revealLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PERSON_BLURRED_WORK_ROWS = [
  { role: 'Head of Product', company_name: 'Northstar Labs', dateRange: '2023 - Present', description: 'Led product strategy across consumer and enterprise lines.' },
  { role: 'Senior Product Manager', company_name: 'Vertex Systems', dateRange: '2020 - 2023', description: 'Shipped the v3 platform redesign and drove ARR from $4M to $18M.' },
  { role: 'Product Manager', company_name: 'Summit Cloud', dateRange: '2017 - 2020', description: 'Owned billing and growth surfaces, partnered with sales on enterprise deals.' },
];

const PERSON_BLURRED_EDUCATION_ROWS = [
  { school: 'Stanford University', major: 'B.S. Computer Science', dateRange: '2013 - 2017' },
  { school: 'Phillips Exeter Academy', major: 'High School Diploma', dateRange: '2009 - 2013' },
];

const PersonSectionPlaceholderRows = ({ rows, kind }) => (
  <div className="space-y-5 blur-sm select-none pointer-events-none">
    {rows.map((entry, i) => (
      <div key={i} className="flex gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-sm">
            {(kind === 'work' ? entry.company_name : entry.school).charAt(0)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-900">{kind === 'work' ? entry.role : entry.school}</div>
          <div className="text-sm text-zinc-700">{kind === 'work' ? entry.company_name : entry.major}</div>
          <div className="text-xs text-zinc-400 mt-0.5">{entry.dateRange}</div>
          {kind === 'work' && entry.description && (
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{entry.description}</p>
          )}
        </div>
      </div>
    ))}
  </div>
);

const WorkExperienceSection = ({ items, revealed = true }) => {
  const companyHref = IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/';
  if (revealed && (!items || items.length === 0)) return null;
  if (!revealed) {
    return (
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-zinc-900 mb-4 flex items-center gap-1.5">
          <i className="ph ph-briefcase text-purple-600"></i>
          Work experience
        </h3>
        <PersonSectionPlaceholderRows rows={PERSON_BLURRED_WORK_ROWS} kind="work" />
      </div>
    );
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6">
      <h3 className="text-sm font-semibold text-zinc-900 mb-4 flex items-center gap-1.5">
        <i className="ph ph-briefcase text-purple-600"></i>
        Work experience
      </h3>
      <div className="space-y-5">
        {items.map((exp, i) => {
          const isCurrent = exp.is_current === true;
          const dateRange = formatDateRange(exp.start_date, exp.end_date, isCurrent);
          return (
            <div key={i} className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {exp.company_website ? (
                  window.CompanyAvatar
                    ? <CompanyAvatar name={exp.company_name} domain={exp.company_website} size="md" />
                    : <img src={`https://www.google.com/s2/favicons?domain=${exp.company_website}&sz=128`} alt={exp.company_name} className="w-10 h-10 rounded-lg" />
                ) : (
                  <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-sm">
                    {(exp.company_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-zinc-900">{exp.role}</div>
                  {isCurrent && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded">
                      Current
                    </span>
                  )}
                </div>
                <div className="text-sm text-zinc-700">
                  {exp.company_website ? (
                    <a href={`${companyHref}${encodeURIComponent(exp.company_website)}`} className="text-purple-600 hover:underline">
                      {exp.company_name}
                    </a>
                  ) : (
                    exp.company_name
                  )}
                </div>
                {dateRange && <div className="text-xs text-zinc-400 mt-0.5">{dateRange}</div>}
                {exp.description && (
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{exp.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EducationSection = ({ items, revealed = true }) => {
  if (revealed && (!items || items.length === 0)) return null;
  if (!revealed) {
    return (
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-zinc-900 mb-4 flex items-center gap-1.5">
          <i className="ph ph-graduation-cap text-purple-600"></i>
          Education
        </h3>
        <PersonSectionPlaceholderRows rows={PERSON_BLURRED_EDUCATION_ROWS} kind="education" />
      </div>
    );
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6">
      <h3 className="text-sm font-semibold text-zinc-900 mb-4 flex items-center gap-1.5">
        <i className="ph ph-graduation-cap text-purple-600"></i>
        Education
      </h3>
      <div className="space-y-4">
        {items.map((edu, i) => {
          const dateRange = formatDateRange(edu.start_date, edu.end_date, false);
          return (
            <div key={i} className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
                <i className="ph ph-graduation-cap text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900">{edu.school}</div>
                {edu.major && <div className="text-sm text-zinc-700">{edu.major}</div>}
                {dateRange && <div className="text-xs text-zinc-400 mt-0.5">{dateRange}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const currentPersonRole = (profile) => {
  const work = Array.isArray(profile?.work_experience) ? profile.work_experience : [];
  return work[0] || {};
};

const SimilarPersonResultCard = ({ profile }) => {
  const companyHref = IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/';
  const currentRole = currentPersonRole(profile);
  const name = profile.full_name || profile.name || 'Unknown person';
  const title = currentRole.role || profile.title || '';
  const company = currentRole.company_name || profile.company || '';
  const companyDomain = currentRole.company_website || profile.company_domain || profile.companyDomain || '';
  const personHref = profile.slug
    ? `${DASHBOARD_EXPLORER_BASE}/person/${encodeURIComponent(profile.slug)}`
    : null;

  return (
    <div className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-zinc-50 transition-colors">
      <PersonAvatar name={name} profilePicUrl={profile.profile_pic_url} size="sm" />
      <div className="min-w-0 flex-1">
        {personHref ? (
          <a href={personHref} className="font-medium text-sm text-zinc-900 hover:text-purple-600 truncate block">
            {name}
          </a>
        ) : (
          <div className="font-medium text-sm text-zinc-900 truncate">{name}</div>
        )}
        <div className="text-xs text-zinc-500 truncate">
          {title}{title && company ? ' at ' : ''}{company}
        </div>
        {companyDomain && (
          <a
            href={`${companyHref}${encodeURIComponent(companyDomain)}`}
            className="no-btn-shadow inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-0.5"
          >
            <i className="ph ph-buildings text-xs"></i>
            {company || companyDomain}
          </a>
        )}
      </div>
      {personHref && (
        <a href={personHref} className="text-zinc-300 hover:text-purple-600 flex-shrink-0" title="Open person profile">
          <i className="ph ph-arrow-right text-sm"></i>
        </a>
      )}
    </div>
  );
};

const SIMILAR_PEOPLE_PLACEHOLDERS = [
  { name: 'Alicia Morgan', title: 'Revenue leader', company: 'Northstar Labs' },
  { name: 'Daniel Park', title: 'Growth advisor', company: 'Summit Cloud' },
  { name: 'Priya Raman', title: 'Product executive', company: 'Vertex Systems' },
  { name: 'Marcus Lee', title: 'Operations lead', company: 'Signal Works' },
];

const SIMILAR_PEOPLE_INITIAL_LIMIT = 5;
const SIMILAR_PEOPLE_EXPANDED_LIMIT = 10;

const SimilarPeoplePlaceholderRows = ({ count = 3 }) => (
  <div className="space-y-2 blur-[1.5px] opacity-80 select-none pointer-events-none">
    {SIMILAR_PEOPLE_PLACEHOLDERS.slice(0, count).map((person, idx) => (
      <div key={idx} className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white/95 px-3 py-2.5 shadow-sm">
        <div className="h-8 w-8 flex-shrink-0 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300"></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-zinc-700 truncate">{person.name}</div>
          <div className="text-xs text-zinc-500 truncate">{person.title} at {person.company}</div>
        </div>
        <i className="ph ph-arrow-right text-zinc-300 text-sm"></i>
      </div>
    ))}
  </div>
);

const SimilarPeopleLoadPreview = ({ onLoad, previewPeople = [] }) => {
  const visiblePreview = (previewPeople || []).slice(0, 2);
  const placeholderCount = visiblePreview.length > 0 ? 3 : 4;
  return (
    <div className="space-y-2">
      {visiblePreview.length > 0 && (
        <div className="space-y-1">
          {visiblePreview.map((profile, index) => (
            <SimilarPersonResultCard
              key={profile.slug || profile.id || `${profile.full_name || profile.name || 'preview'}-${index}`}
              profile={profile}
            />
          ))}
        </div>
      )}
      <div className="relative overflow-hidden rounded-xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50 p-2">
        <SimilarPeoplePlaceholderRows count={placeholderCount} />
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-white via-white/80 to-white/25">
          <button
            onClick={onLoad}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700"
          >
            <i className="ph ph-play text-sm"></i>
            Load similar people
          </button>
        </div>
      </div>
    </div>
  );
};

const SimilarPeopleCard = ({ person }) => {
  const [visibleLimit, setVisibleLimit] = useState(SIMILAR_PEOPLE_INITIAL_LIMIT);
  const [cacheStatus, setCacheStatus] = useState({
    loading: true,
    has_cached_data: false,
    cached_for_product: false,
    similar_count: 0,
    preview_people: [],
  });
  const prospectorJob = useProfileProspectorJob();
  const hydrateProspectorJob = prospectorJob.hydrate;
  const prospectorSheetId = prospectorJob.jobState?.sheet?.id || null;

  useEffect(() => {
    if (!person?.slug) return undefined;

    let cancelled = false;
    setCacheStatus({
      loading: true,
      has_cached_data: false,
      cached_for_product: false,
      similar_count: 0,
      preview_people: [],
    });
    ExplorerApi.getSimilarPeopleStatus(person.slug)
      .then(data => {
        if (cancelled) return;
        const similarCount = Number(data.similar_count || 0);
        setCacheStatus({
          loading: false,
          has_cached_data: data.success === true && data.has_cached_data === true,
          cached_for_product: data.success === true && data.cached_for_product === true,
          similar_count: Number.isFinite(similarCount) ? similarCount : 0,
          preview_people: Array.isArray(data.preview_people) ? data.preview_people.slice(0, 2) : [],
        });
        if (data.success && data.loaded_sheet) {
          hydrateProspectorJob({
            sheet: data.loaded_sheet,
            redirect_url: data.redirect_url,
            job: data.job,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCacheStatus({
            loading: false,
            has_cached_data: false,
            cached_for_product: false,
            similar_count: 0,
            preview_people: [],
          });
        }
      });

    return () => { cancelled = true; };
  }, [person?.slug, hydrateProspectorJob]);

  useEffect(() => {
    setVisibleLimit(SIMILAR_PEOPLE_INITIAL_LIMIT);
  }, [person?.slug, prospectorSheetId]);

  if (!person?.slug) return null;

  const people = (prospectorJob.rows || []).map((row) => ({
    ...row,
    full_name: row.full_name || row.name,
    title: row.title,
    company: row.company,
    company_domain: row.company_domain,
  }));
  const hasLoadedResults = people.length > 0;
  const hasCachedResults = cacheStatus.has_cached_data && cacheStatus.similar_count > 0;
  const resultCount = hasLoadedResults || prospectorJob.completed
    ? people.length
    : (hasCachedResults ? cacheStatus.similar_count : null);
  const prospectorHref = prospectorJob.jobState?.redirect_url;
  const cappedVisibleLimit = Math.min(visibleLimit, SIMILAR_PEOPLE_EXPANDED_LIMIT);
  const visiblePeople = people.slice(0, cappedVisibleLimit);
  const canShowMore = visiblePeople.length < people.length && visiblePeople.length < SIMILAR_PEOPLE_EXPANDED_LIMIT;
  const showInlineCount = people.length > SIMILAR_PEOPLE_INITIAL_LIMIT;

  const loadSimilarPeople = () => {
    prospectorJob.start(() => ExplorerApi.openSimilarPeopleProspector(person.slug));
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
          <i className="ph ph-users-three text-purple-600"></i>
          Similar people
        </h3>
        {resultCount !== null && (
          <span className="text-[11px] font-medium text-zinc-400">{resultCount} found</span>
        )}
      </div>

      {!prospectorJob.active && !prospectorJob.queueing && !prospectorJob.completed && !prospectorJob.error && (
        <div className="space-y-3">
          <SimilarPeopleLoadPreview
            onLoad={loadSimilarPeople}
            previewPeople={cacheStatus.preview_people}
          />
        </div>
      )}

      {(prospectorJob.active || prospectorJob.queueing) && (
        <ProspectorProgressPanel
          title="Finding similar people"
          href={prospectorHref}
          progress={prospectorJob.progress}
          elapsedSeconds={prospectorJob.elapsedSeconds}
        />
      )}

      {prospectorJob.completed && (
        <div className="space-y-3">
          {people.length > 0 ? (
            <div className="space-y-2">
              <div className="space-y-1">
                {visiblePeople.map((profile, index) => (
                  <SimilarPersonResultCard
                    key={profile.slug || profile.id || `${profile.full_name || profile.name || 'person'}-${index}`}
                    profile={profile}
                  />
                ))}
              </div>
              {showInlineCount && (
                <div className="flex items-center justify-between gap-3 pt-1 text-xs text-zinc-500">
                  <span>Showing {visiblePeople.length} of {people.length}</span>
                  {canShowMore && (
                    <button
                      type="button"
                      onClick={() => setVisibleLimit(SIMILAR_PEOPLE_EXPANDED_LIMIT)}
                      className="no-btn-shadow inline-flex items-center gap-1.5 font-medium text-purple-600 hover:text-purple-700 transition-colors"
                    >
                      <i className="ph ph-caret-down text-sm"></i>
                      Show more
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg bg-zinc-50 px-3 py-3 text-xs text-zinc-500">
              No similar people found.
            </div>
          )}
          <ProspectorQueuedFooter href={prospectorHref} label="View all in Prospector" />
        </div>
      )}

      {prospectorJob.error && !prospectorJob.active && !prospectorJob.queueing && (
        <ErrorPanel
          error={prospectorJob.error}
          onRetry={loadSimilarPeople}
          retrying={prospectorJob.queueing}
        />
      )}
    </div>
  );
};

const DiscoverPeople = ({ currentSlug }) => {
  const [people, setPeople] = useState([]);

  useEffect(() => {
    if (!IS_DASHBOARD) return;
    ExplorerApi.getDiscoverPeople(currentSlug)
      .then(data => setPeople(data.people || []))
      .catch(() => setPeople([]));
  }, [currentSlug]);

  if (people.length === 0) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4">
      <h3 className="text-sm font-semibold text-zinc-900 mb-3">Discover people</h3>
      <div className="space-y-3">
        {people.map(p => (
          <a
            key={p.slug}
            href={`${DASHBOARD_EXPLORER_BASE}/person/${encodeURIComponent(p.slug)}`}
            className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-zinc-50 transition-colors"
          >
            <PersonAvatar name={p.name} profilePicUrl={p.profile_pic_url} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm text-zinc-900 truncate">{p.name}</div>
              <div className="text-xs text-zinc-400 truncate">
                {p.title}{p.title && p.company ? ' at ' : ''}{p.company}
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

const PersonProfilePage = ({ slug }) => {
  const [person, setPerson] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);

  const applyFullProfile = useCallback((profile) => {
    if (!profile) return;
    const effectiveSlug = profile.slug || slug;
    const lookupWorkEmail = consumePersonLookupEmail(effectiveSlug);
    setPerson(lookupWorkEmail
      ? { ...profile, work_email: lookupWorkEmail, work_email_source: 'lookup_input' }
      : profile
    );
    setRevealed(true);
  }, [slug]);

  const fetchFullProfile = useCallback(() => {
    setRevealLoading(true);
    setRevealError(null);
    return ExplorerApi.getPersonFullProfile(slug)
      .then(data => {
        if (data.success && data.profile) {
          applyFullProfile(data.profile);
          setEnrichmentStatus(data.enrichment_status || 'complete');
          return true;
        }
        setRevealError(data.error || 'Failed to load person profile');
        return false;
      })
      .catch((e) => {
        setRevealError(e?.message || 'Failed to load person profile');
        return false;
      })
      .finally(() => setRevealLoading(false));
  }, [slug, applyFullProfile]);

  const loadPerson = useCallback(() => {
    if (!slug) {
      setError('Missing person profile slug');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setRevealed(false);
    setPerson(null);
    setRevealError(null);
    setEnrichmentStatus(null);

    ExplorerApi.getPersonSummary(slug)
      .then(data => {
        if (!data.success || !data.profile) {
          setError(data.error || 'Person profile not found');
          return;
        }
        setPerson(data.profile);
        setEnrichmentStatus(data.enrichment_status || 'complete');

        const alwaysReveal = localStorage.getItem('explorer_always_reveal_person') === 'true';
        if (alwaysReveal) {
          fetchFullProfile();
          return;
        }

        ExplorerApi.checkSavedPersonProfile(slug).then(saved => {
          if (saved && saved.has_saved_profile) fetchFullProfile();
        }).catch(() => {});
      })
      .catch((e) => setError(e?.message || 'Failed to load person profile.'))
      .finally(() => setLoading(false));
  }, [slug, fetchFullProfile]);

  useEffect(() => { loadPerson(); }, [loadPerson]);

  useEffect(() => {
    if (enrichmentStatus !== 'pending' || !slug) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timeoutId = null;
    const maxAttempts = 30;
    const intervalMs = 3000;

    const poll = () => {
      timeoutId = setTimeout(() => {
        attempts += 1;
        const request = revealed
          ? ExplorerApi.getPersonFullProfile(slug)
          : ExplorerApi.getPersonSummary(slug);
        request.then(data => {
          if (cancelled) return;
          if (!data.success || !data.profile) {
            setEnrichmentStatus('complete');
            return;
          }
          setPerson(prev => ({
            ...(revealed ? {} : (prev || {})),
            ...data.profile,
            ...(prev?.work_email_source === 'lookup_input'
              ? { work_email: prev.work_email, work_email_source: prev.work_email_source }
              : {}),
          }));
          const status = data.enrichment_status || 'complete';
          if (status === 'pending' && attempts < maxAttempts) {
            poll();
          } else {
            setEnrichmentStatus('complete');
          }
        });
      }, intervalMs);
    };
    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [enrichmentStatus, revealed, slug]);

  const handleRevealProfile = (always) => {
    if (always) localStorage.setItem('explorer_always_reveal_person', 'true');
    fetchFullProfile();
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <SkeletonBar className="h-4 w-44" />
        </div>
        <div className="mb-6 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <SkeletonBar className="h-16 w-16 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0 space-y-2.5">
              <SkeletonBar className="h-5 w-1/2" />
              <SkeletonBar className="h-4 w-2/3" />
              <SkeletonBar className="h-3 w-1/3" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
              <div className="mb-4"><SkeletonBar className="h-4 w-40" /></div>
              <SkeletonCardBody rows={3} variant="logoText" />
            </div>
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
              <div className="mb-4"><SkeletonBar className="h-4 w-32" /></div>
              <SkeletonCardBody rows={2} variant="logoText" />
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
              <div className="mb-4"><SkeletonBar className="h-4 w-36" /></div>
              <SkeletonCardBody rows={3} variant="avatarText" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="w-full">
        <a
          href={DASHBOARD_EXPLORER_BASE}
          className="no-btn-shadow inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-purple-600 transition-colors mb-6"
        >
          <i className="ph ph-arrow-left"></i>
          Back to Data Explorer
        </a>
        <ErrorPanel
          error={error || 'Person profile not found'}
          onRetry={loadPerson}
          retrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <a
          href={DASHBOARD_EXPLORER_BASE}
          className="no-btn-shadow inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-purple-600 transition-colors"
        >
          <i className="ph ph-arrow-left"></i>
          Back to Data Explorer
        </a>
      </div>

      <div className="mb-6">
        <SmartSearchBar />
      </div>

      {enrichmentStatus === 'pending' && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-50 border border-purple-200 px-3 py-1.5 text-xs font-medium text-purple-700">
          <i className="ph ph-circle-notch animate-spin"></i>
          Enriching profile…
        </div>
      )}

      <div className="mb-6">
        <PersonHeaderCard
          person={person}
          revealed={revealed}
          onReveal={handleRevealProfile}
          revealLoading={revealLoading}
          revealError={revealError}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <WorkExperienceSection items={person.work_experience} revealed={revealed} />
          <EducationSection items={person.education} revealed={revealed} />
        </div>
        <div className="space-y-6">
          {revealed && <SimilarPeopleCard person={person} />}
          <DiscoverPeople currentSlug={person.slug} />
        </div>
      </div>
    </div>
  );
};

const PersonLookupPage = ({ lookup }) => {
  const [lookupError, setLookupError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);

  const personLabel = (lookup?.work_email
    || [lookup?.first_name, lookup?.last_name].filter(Boolean).join(' ')
    || lookup?.role
    || 'this contact');
  const employer = (lookup?.employer_website || '').trim();

  const stages = useMemo(() => {
    const arr = [];
    if (lookup?.work_email) {
      arr.push(`Resolving ${lookup.work_email}…`);
    } else if (lookup?.first_name || lookup?.last_name) {
      arr.push(`Searching for ${[lookup?.first_name, lookup?.last_name].filter(Boolean).join(' ')}…`);
    } else if (lookup?.role) {
      arr.push(`Searching for ${lookup.role}…`);
    } else {
      arr.push('Searching for this contact…');
    }
    if (employer) arr.push(`Matching to ${employer}…`);
    arr.push('Pulling profile, work history, and links…');
    arr.push('Almost there…');
    return arr;
  }, [lookup, employer]);

  useEffect(() => {
    const payload = {};
    Object.entries(lookup || {}).forEach(([key, value]) => {
      const clean = String(value || '').trim();
      if (clean) payload[key] = clean;
    });

    if (!payload.work_email && !(payload.first_name && payload.employer_website) && !(payload.employer_website && payload.role)) {
      setLookupError({ type: 'generic', message: 'Missing person lookup input.' });
      return;
    }

    const startTime = Date.now();
    const totalMs = 12000;
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / totalMs, 1);
      const p = (1 - Math.exp(-3 * t)) * 95;
      setProgress(Math.min(Math.round(p), 95));
    }, 100);

    const stageInterval = setInterval(() => {
      setStageIndex(i => Math.min(i + 1, stages.length - 1));
    }, 2200);

    ExplorerApi.lookupPerson(payload)
      .then(data => {
        clearInterval(progressInterval);
        clearInterval(stageInterval);
        setProgress(100);
        if (data.success && data.redirect_url) {
          let redirectUrl = data.redirect_url;
          const profileSlug = data.profile?.slug || extractPersonSlugFromRedirect(redirectUrl);
          if (payload.work_email && profileSlug) {
            const lookupRef = storePersonLookupEmail(profileSlug, payload.work_email);
            redirectUrl = addPersonLookupRefToUrl(redirectUrl, lookupRef);
          }
          window.location.replace(redirectUrl);
        } else if (data.error_code === 'NO_PROFILE_DATA') {
          setLookupError({
            type: 'no_profile',
            message: data.error || 'No profile data found',
            errorCode: data.error_code,
            httpStatus: data.http_status,
          });
        } else {
          setLookupError({
            type: 'generic',
            message: data.error || 'Person lookup failed.',
            errorCode: data.error_code,
            httpStatus: data.http_status,
          });
        }
      })
      .catch(() => {
        clearInterval(progressInterval);
        clearInterval(stageInterval);
        setLookupError({ type: 'generic', message: 'Person lookup failed. Please try again.' });
      });

    return () => {
      clearInterval(progressInterval);
      clearInterval(stageInterval);
    };
  }, [lookup, stages.length]);

  if (lookupError) {
    return (
      <div className="w-full">
        <a
          href={DASHBOARD_EXPLORER_BASE}
          className="no-btn-shadow inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-purple-600 transition-colors mb-6"
        >
          <i className="ph ph-arrow-left"></i>
          Back to Data Explorer
        </a>
        {lookupError.type === 'no_profile' ? (
          <NoProfileFoundPanel />
        ) : (
          <ErrorPanel
            error={lookupError.message}
            onRetry={() => window.location.reload()}
          />
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <a
          href={DASHBOARD_EXPLORER_BASE}
          className="no-btn-shadow inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-purple-600 transition-colors"
        >
          <i className="ph ph-arrow-left"></i>
          Back to Data Explorer
        </a>
      </div>

      <div className="mb-4 bg-white border border-zinc-200 rounded-2xl px-5 py-4 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-medium text-zinc-700 mb-2">
          <i className="ph ph-circle-notch animate-spin text-purple-600 text-base"></i>
          <span>{stages[stageIndex]}</span>
          <span className="ml-auto tabular-nums text-zinc-400">{progress}%</span>
        </div>
        <div className="w-full bg-zinc-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-gradient-to-r from-purple-500 to-purple-600 h-1.5 rounded-full transition-all duration-200 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-2.5 text-xs text-zinc-500">
          Looking up <span className="font-medium text-zinc-700">{personLabel}</span>
          {employer ? <> at <span className="font-medium text-zinc-700">{employer}</span></> : null}.
        </p>
      </div>

      <div className="mb-6 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <SkeletonBar className="h-16 w-16 rounded-full flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-2.5">
            <SkeletonBar className="h-5 w-1/2" />
            <SkeletonBar className="h-4 w-2/3" />
            <SkeletonBar className="h-3 w-1/3" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
            <div className="mb-4"><SkeletonBar className="h-4 w-40" /></div>
            <SkeletonCardBody rows={3} variant="logoText" />
          </div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
            <div className="mb-4"><SkeletonBar className="h-4 w-32" /></div>
            <SkeletonCardBody rows={2} variant="logoText" />
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
            <div className="mb-4"><SkeletonBar className="h-4 w-36" /></div>
            <SkeletonCardBody rows={3} variant="avatarText" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Dashboard Landing (Mock UI) ──────────────────────────────────────

const detectInputType = DataExplorerSearch.detectInputType;

const SmartSearchBar = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [personCompany, setPersonCompany] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const inputRef = useRef(null);
  const companyInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const q = query.trim();
    const type = detectInputType(q);
    if (q.length < 2 || type === 'email') {
      setCompanyResults([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    const timer = setTimeout(() => {
      ExplorerApi.searchCompanies(q)
        .then(data => {
          if (!cancelled) setCompanyResults((data.results || []).slice(0, 6));
        })
        .catch((error) => {
          console.error('Explorer company search failed:', error);
          if (!cancelled) setCompanyResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const suggestions = DataExplorerSearch.buildIntentSuggestions(query);
  const companySuggestions = DataExplorerSearch.buildCompanySuggestions(companyResults);
  const allRows = [...suggestions, ...companySuggestions];
  const totalRows = allRows.length;

  const handleSelect = (item) => {
    const closeAndClear = () => {
      setQuery('');
      setPersonCompany('');
      setIsOpen(false);
      setSelectedIndex(-1);
    };

    const action = DataExplorerSearch.getSelectionAction(item, { personCompany });
    if (action.type === 'needs-person-company') {
      setIsOpen(true);
      setSelectedIndex(0);
      window.requestAnimationFrame(() => companyInputRef.current?.focus());
      return;
    }

    closeAndClear();
    DataExplorerSearch.navigateAction(action, { dashboard: IS_DASHBOARD });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setIsOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, totalRows - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleSelect(allRows[selectedIndex]);
      } else {
        const defaultItem = DataExplorerSearch.getDefaultSubmitItem(query, companySuggestions);
        if (defaultItem) handleSelect(defaultItem);
      }
    }
  };

  const showDropdown = isOpen && totalRows > 0;

  return (
    <div className="relative w-full">
      <div className="relative">
        <i className="ph ph-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400 text-lg pointer-events-none"></i>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setSelectedIndex(-1); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search by name, work email, or company website…"
          className="w-full px-6 py-5 pl-14 pr-6 text-lg border border-zinc-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-zinc-400 text-zinc-800"
        />
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-200 rounded-2xl shadow-xl z-50 overflow-hidden"
        >
          {suggestions.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[11px] font-semibold text-zinc-500 bg-zinc-50 border-b border-zinc-100 uppercase tracking-wider">
                Suggestions
              </div>
              {suggestions.map((s, idx) => {
                const isSelected = idx === selectedIndex;
                if (s.kind === 'person-name') {
                  return (
                    <div
                      key={s.kind}
                      className={`border-b border-zinc-100 last:border-b-0 ${isSelected ? 'bg-purple-50' : ''}`}
                    >
                      <div
                        onClick={() => handleSelect(s)}
                        className={`px-5 py-3 flex items-center gap-3 cursor-pointer ${isSelected ? '' : 'hover:bg-zinc-50'}`}
                      >
                        <i className={`ph ${s.icon} text-zinc-500 text-lg`}></i>
                        <div className="text-sm text-zinc-700">
                          {s.label} <span className="font-semibold text-zinc-900">"{s.highlight}"</span>
                        </div>
                      </div>
                      <div className="pl-12 pr-5 pb-4 flex items-center gap-2">
                        <span className="text-xs text-zinc-500 flex-shrink-0">at</span>
                        <input
                          ref={companyInputRef}
                          type="text"
                          value={personCompany}
                          onChange={(e) => setPersonCompany(e.target.value)}
                          placeholder="company name or website"
                          className="flex-1 px-3 py-1.5 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent placeholder-zinc-400 bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => handleSelect({ ...s, company: personCompany })}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 flex-shrink-0"
                        >
                          Find <i className="ph ph-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={s.kind}
                    onClick={() => handleSelect(s)}
                    className={`px-5 py-3 cursor-pointer flex items-center gap-3 border-b border-zinc-100 last:border-b-0 ${isSelected ? 'bg-purple-50' : 'hover:bg-zinc-50'}`}
                  >
                    <i className={`ph ${s.icon} text-zinc-500 text-lg`}></i>
                    <div className="text-sm text-zinc-700 truncate">
                      {s.label} <span className="font-semibold text-zinc-900">"{s.highlight}"</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {(companySuggestions.length > 0 || searchLoading) && (
            <div>
              <div className="px-4 py-2 text-[11px] font-semibold text-zinc-500 bg-zinc-50 border-b border-zinc-100 uppercase tracking-wider">
                Companies
              </div>
              {searchLoading && companySuggestions.length === 0 && (
                <div className="px-5 py-3 flex items-center gap-2 text-sm text-zinc-500">
                  <i className="ph ph-spinner-gap animate-spin"></i>
                  Searching companies...
                </div>
              )}
              {companySuggestions.map((s, idx) => {
                const flatIdx = suggestions.length + idx;
                const isSelected = flatIdx === selectedIndex;
                return (
                  <div
                    key={s.slug || s.domain}
                    onClick={() => handleSelect(s)}
                    className={`px-5 py-3 cursor-pointer flex items-center gap-3 border-b border-zinc-100 last:border-b-0 ${isSelected ? 'bg-purple-50' : 'hover:bg-zinc-50'}`}
                  >
                    {window.CompanyAvatar ? (
                      <CompanyAvatar name={s.name} domain={s.domain} size="sm" />
                    ) : (
                      <div className="w-8 h-8 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-xs">
                        {(s.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate">{s.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{s.domain}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const dashboardPersonHref = (person) => (
  person?.href
  || DataExplorerSearch.getPersonSlugHref?.(person?.slug)
  || (person?.slug ? `${DASHBOARD_EXPLORER_BASE}/person/${encodeURIComponent(person.slug)}` : '')
);

const dashboardCompanyHref = (company) => (
  company?.href
  || DataExplorerSearch.getCompanyHref?.(company?.slug || company?.domain, { dashboard: IS_DASHBOARD })
  || ((company?.slug || company?.domain) ? `${DASHBOARD_EXPLORER_BASE}/${encodeURIComponent(company.slug || company.domain)}` : '')
);

const ShortcutCard = ({ kind, name, subtitle, domain, href, profilePicUrl }) => {
  const isPerson = kind === 'person';
  const badgeClass = isPerson ? 'bg-purple-100 text-purple-700' : 'bg-zinc-100 text-zinc-700';
  const badgeLabel = isPerson ? 'Person' : 'Company';
  const cardHref = href || (isPerson
    ? DataExplorerSearch.getPersonLookupHref?.({ work_email: 'steven@nubela.co' })
    : DataExplorerSearch.getCompanyHref?.(domain, { dashboard: IS_DASHBOARD }));
  return (
    <a
      href={cardHref || '#'}
      className="block text-left bg-white border border-zinc-200 rounded-xl p-4 hover:border-green-300 hover:shadow-md transition-all relative group"
    >
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badgeClass}`}>{badgeLabel}</span>
      </div>
      <div className="flex items-center gap-3">
        {isPerson ? (
          <PersonAvatar name={name} profilePicUrl={profilePicUrl} size="md" />
        ) : (
          window.CompanyAvatar
            ? <CompanyAvatar name={name} domain={domain} size="md" />
            : <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} alt={name} className="w-12 h-12 rounded-xl flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="font-semibold text-zinc-900 group-hover:text-purple-600 transition-colors truncate">{name}</div>
          <div className="text-xs text-zinc-500 truncate">{subtitle}</div>
        </div>
      </div>
      <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1">
        <i className="ph ph-arrow-right"></i> Try this search
      </div>
    </a>
  );
};

const RecentPeopleList = ({ people }) => {
  if (!people || people.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-zinc-700 mb-3 flex items-center gap-2">
        <i className="ph ph-user text-zinc-500"></i> Recent people
      </h3>
      <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
        {people.map(p => (
          <a
            key={p.slug || p.id || p.name}
            href={dashboardPersonHref(p)}
            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
          >
            <PersonAvatar name={p.name} profilePicUrl={p.profile_pic_url} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-900 truncate">{p.name}</div>
              <div className="text-xs text-zinc-500 truncate">
                {[p.title, p.company].filter(Boolean).join(' at ')}
              </div>
            </div>
            <i className="ph ph-arrow-right text-zinc-300 text-sm"></i>
          </a>
        ))}
      </div>
    </div>
  );
};

const RecentCompaniesList = ({ companies }) => {
  if (!companies || companies.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-zinc-700 mb-3 flex items-center gap-2">
        <i className="ph ph-buildings text-zinc-500"></i> Recent companies
      </h3>
      <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
        {companies.map(c => (
          <a
            key={c.slug || c.domain}
            href={dashboardCompanyHref(c)}
            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
          >
            {window.CompanyAvatar ? (
              <CompanyAvatar name={c.name} domain={c.domain} size="sm" />
            ) : (
              <div className="w-8 h-8 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-xs flex-shrink-0">
                {(c.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-900 truncate">{c.name}</div>
              <div className="text-xs text-zinc-500 truncate">{c.domain}</div>
            </div>
            <i className="ph ph-arrow-right text-zinc-300 text-sm"></i>
          </a>
        ))}
      </div>
    </div>
  );
};

const ExplorerSearchPageDashboard = () => {
  const [recentPeople, setRecentPeople] = useState([]);
  const [recentCompanies, setRecentCompanies] = useState([]);

  useEffect(() => {
    ExplorerApi.getRecentlyViewed()
      .then(data => {
        setRecentPeople((data.people || []).slice(0, 10));
        setRecentCompanies((data.companies || []).slice(0, 10));
      })
      .catch(() => {
        setRecentPeople([]);
        setRecentCompanies([]);
      });
  }, []);

  const showRecent = recentPeople.length > 0 || recentCompanies.length > 0;
  const stevenShortcut = EXPLORER_SHORTCUT_CARDS.steven_goh || {};
  const stripeShortcut = EXPLORER_SHORTCUT_CARDS.stripe || {};

  return (
    <div className="w-full max-w-4xl mx-auto">
      {IS_DASHBOARD && window.EXPLORER_ONBOARDING_ENABLED && window.ExplorerOnboardingWidget && (
        <window.ExplorerOnboardingWidget />
      )}

      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 border border-purple-100 rounded-full text-sm text-purple-700 font-medium mb-4">
          <i className="ph ph-crosshair"></i>
          Data Explorer
        </div>
        <h1 className="text-3xl font-bold text-zinc-900 mb-2">Who or what are you exploring?</h1>
        <p className="text-zinc-500">
          Search for a person, company, work email, or website - we'll figure it out.
        </p>
      </div>

      <div className="mb-10">
        <SmartSearchBar />
      </div>

      <div className="mb-10">
        <h2 className="text-sm font-semibold text-zinc-700 mb-3">Try a quick search</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ShortcutCard
            kind="person"
            name={stevenShortcut.name || 'Steven Goh'}
            subtitle={stevenShortcut.subtitle || 'CEO at NinjaPear'}
            href={stevenShortcut.href || `${DASHBOARD_EXPLORER_BASE}/person/steven-goh`}
            profilePicUrl={stevenShortcut.profile_pic_url}
          />
          <ShortcutCard
            kind="company"
            name={stripeShortcut.name || 'Stripe'}
            subtitle={stripeShortcut.subtitle || 'stripe.com'}
            domain={stripeShortcut.domain || 'stripe.com'}
            href={stripeShortcut.href || dashboardCompanyHref({ domain: 'stripe.com' })}
          />
        </div>
      </div>

      {showRecent && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <RecentPeopleList people={recentPeople} />
          <RecentCompaniesList companies={recentCompanies} />
        </div>
      )}
    </div>
  );
};

// ─── Pages ────────────────────────────────────────────────────────────

const ExplorerSearchPage = () => {
  if (IS_DASHBOARD) return <ExplorerSearchPageDashboard />;
  return <ExplorerSearchPagePublic />;
};

const ExplorerSearchPagePublic = () => {
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [browseCompanies, setBrowseCompanies] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(true);
  const [browsePage, setBrowsePage] = useState(1);
  const [browsePagination, setBrowsePagination] = useState({ has_next: false, has_prev: false, total_pages: 1, total_count: 0 });
  const browseRef = useRef(null);

  const fetchBrowseCompanies = (page) => {
    setBrowseLoading(true);
    const limit = IS_DASHBOARD ? 12 : null;
    ExplorerApi.getCompanyIndex(page, limit)
      .then(data => {
        setBrowseCompanies(data.companies || []);
        setBrowsePagination({ has_next: data.has_next, has_prev: data.has_prev, total_pages: data.total_pages, total_count: data.total_count });
        setBrowsePage(data.page);
        if (page > 1 && browseRef.current) {
          browseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch(() => {})
      .finally(() => setBrowseLoading(false));
  };

  useEffect(() => {
    // Fetch recently viewed from Redis
    ExplorerApi.getRecentlyViewed()
      .then(data => setRecentCompanies(data.companies || []))
      .catch(() => {});

    fetchBrowseCompanies(1);
  }, []);

  return (
    <div className="w-full">
      {/* Explorer Onboarding Widget */}
      {IS_DASHBOARD && window.EXPLORER_ONBOARDING_ENABLED && window.ExplorerOnboardingWidget && (
        <window.ExplorerOnboardingWidget />
      )}

      {/* Hero + Search */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 border border-purple-100 rounded-full text-sm text-purple-700 font-medium mb-4">
          <i className="ph ph-crosshair"></i>
          Data Explorer
        </div>
        <h1 className="text-3xl font-bold text-zinc-900 mb-2">Competitive Intelligence Data Explorer</h1>
        <p className="text-zinc-500 mb-6">
          Explore any company's customers, competitors, and latest updates.
        </p>
        <IQSearchBar currentDomain="" />
        <p className="text-sm text-zinc-500 mt-3">
          <i className="ph ph-info mr-1"></i>
          Enter any company website URL to discover their customers, competitors, and timeline (e.g., stripe.com)
        </p>
      </div>

      {/* Try for Free */}
      <div className="mb-10">
        <h2 className="text-lg font-semibold text-zinc-900 mb-2">Try for Free</h2>
        <p className="text-zinc-500 text-sm mb-4">Explore these companies at no cost - no credits required.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {FREE_DOMAINS.map((domain) => {
            const href = IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/${domain}` : `/companies/${domain}`;
            const logoUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
            return (
              <a
                key={domain}
                href={href}
                className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-green-300 hover:shadow-md transition-all group relative"
              >
                <span className="absolute top-3 right-3 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Free</span>
                <div className="flex items-center gap-3 mb-2">
                  {window.CompanyAvatar ? (
                    <CompanyAvatar name={domain} domain={domain} size="md" />
                  ) : (
                    <img src={logoUrl} alt={domain} className="w-10 h-10 rounded-xl" />
                  )}
                  <div>
                    <div className="font-semibold text-zinc-900 group-hover:text-purple-600 transition-colors">{domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1)}</div>
                    <div className="text-xs text-zinc-400">{domain}</div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Recently Viewed */}
      {recentCompanies.length > 0 && (
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-zinc-900 mb-4">Recently Viewed</h2>
          <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
            {recentCompanies.slice(0, 5).map((company, idx) => (
              <a
                key={company.slug || company.domain || idx}
                href={(IS_DASHBOARD ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/') + encodeURIComponent(company.slug || company.domain)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
              >
                {window.CompanyAvatar ? (
                  <CompanyAvatar name={company.name} domain={company.domain} size="sm" />
                ) : (
                  <div className="w-8 h-8 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-400 font-semibold text-sm">
                    {(company.name || company.domain || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900 text-sm">{company.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{company.domain}</div>
                </div>
                <i className="ph ph-arrow-right text-zinc-300 text-sm"></i>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Browse Companies */}
      <div className="mb-10">
        <h2 ref={browseRef} className="text-lg font-semibold text-zinc-900 mb-4">Browse Companies</h2>
        {browseLoading ? (
          <div className="text-center py-8 text-zinc-400">
            <i className="ph ph-spinner-gap animate-spin text-2xl"></i>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {browseCompanies.map((company) => {
              const href = IS_DASHBOARD
                ? `${DASHBOARD_EXPLORER_BASE}/${encodeURIComponent(company.slug || company.name)}`
                : `/companies/${encodeURIComponent(company.slug || company.name)}`;
              return (
                <a
                  key={company.slug || company.name}
                  href={href}
                  className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start gap-3">
                    {window.CompanyAvatar ? (
                      <CompanyAvatar name={company.name} domain={company.domain || company.slug} size="md" />
                    ) : (
                      company.logo_url ? <img src={company.logo_url} alt={company.name} className="w-10 h-10 rounded-xl" /> :
                      <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center text-zinc-400 font-bold">{(company.name || '?').charAt(0)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-900 group-hover:text-purple-600 transition-colors truncate">{company.name}</div>
                      {company.industry_name && <div className="text-xs text-zinc-400">{company.industry_name}</div>}
                    </div>
                  </div>
                  {company.tagline && <p className="text-xs text-zinc-500 line-clamp-2 mt-2">{company.tagline}</p>}
                </a>
              );
            })}
          </div>
        )}
        {!IS_DASHBOARD && (browsePagination.has_prev || browsePagination.has_next) && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => fetchBrowseCompanies(browsePage - 1)}
              disabled={!browsePagination.has_prev}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ph ph-arrow-left mr-1"></i> Previous
            </button>
            <span className="text-sm text-zinc-500">
              Page {browsePage} of {browsePagination.total_pages}
            </span>
            <button
              onClick={() => fetchBrowseCompanies(browsePage + 1)}
              disabled={!browsePagination.has_next}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <i className="ph ph-arrow-right ml-1"></i>
            </button>
          </div>
        )}
      </div>

      {/* CTA */}
      {!IS_DASHBOARD && (
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 text-white text-center mb-10">
          <h2 className="text-2xl font-bold mb-2">Want to explore any company?</h2>
          <p className="text-purple-100 mb-4">
            Sign up for free and discover customers, competitors, and timeline updates for any company.
          </p>
          <a href="/auth/register" className="inline-block px-8 py-3 bg-white text-purple-700 font-semibold rounded-xl hover:bg-purple-50 transition-colors">
            Start Free Trial
          </a>
        </div>
      )}

      {/* About NinjaPear's Competitive Intelligence Data - public only */}
      {!IS_DASHBOARD && (
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-zinc-900 mb-3">About NinjaPear's Competitive Intelligence Data</h2>
          <p className="text-zinc-600 text-sm leading-relaxed">
            NinjaPear tracks business relationships for thousands of companies. Our database includes
            customer relationships, investor connections, and partner networks. This data helps
            sales teams identify prospects, understand market dynamics, and build targeted outreach campaigns.
            Browse our company directory to discover business relationships and sign up for full access.
          </p>
        </section>
      )}
    </div>
  );
};

const ExplorerProfilePage = ({ slug, domain }) => {
  const [companySummary, setCompanySummary] = useState(null);
  const [fullProfile, setFullProfile] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [autoRevealing, setAutoRevealing] = useState(false);
  const [profileProspectorSheets, setProfileProspectorSheets] = useState(undefined);

  const isFreeCompany = FREE_DOMAINS.includes(domain);

  useEffect(() => {
    if (!IS_DASHBOARD || !domain) {
      setProfileProspectorSheets({});
      return undefined;
    }

    let cancelled = false;
    setProfileProspectorSheets(undefined);
    ExplorerApi.getCompanyProfileProspectorSheets(domain)
      .then(data => {
        if (cancelled) return;
        if (data.success) {
          setProfileProspectorSheets(data.sheets || {});
        } else {
          console.error('Failed to load profile Prospector sheets:', data.error);
          setProfileProspectorSheets({});
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load profile Prospector sheets:', err);
        setProfileProspectorSheets({});
      });

    return () => {
      cancelled = true;
    };
  }, [domain]);

  useEffect(() => {
    if (!slug) return;

    // Record view (fire-and-forget)
    ExplorerApi.recordView(slug);

    // Get free company summary
    ExplorerApi.getCompanySummary(slug)
      .then(data => {
        if (data.success) {
          setCompanySummary(data);
          // Free companies: full profile data is included in the summary response
          if (data.is_free_company && data.full_profile) {
            setFullProfile({ success: true, profile: data.full_profile, credit_cost: 0 });
          }
        }
      })
      .catch(() => {});

    // Dashboard: auto-load full profile
    if (IS_DASHBOARD && domain) {
      if (isFreeCompany) {
        // Free companies on dashboard - auto-reveal (backend serves for free)
        setAutoRevealing(true);
        setRevealLoading(true);
        ExplorerApi.getFullProfile(slug)
          .then(d => {
            if (d.success) setFullProfile(d);
            else { setAutoRevealing(false); }
          })
          .catch(() => { setAutoRevealing(false); })
          .finally(() => setRevealLoading(false));
      } else {
        const alwaysReveal = localStorage.getItem('explorer_always_reveal_details') === 'true';

        if (alwaysReveal) {
          // Known immediately - skip checkSavedProfile, go straight to loading
          setAutoRevealing(true);
          setRevealLoading(true);
          ExplorerApi.getFullProfile(slug)
            .then(d => {
              if (d.success) setFullProfile(d);
              else {
                setAutoRevealing(false);
                setRevealError(d.error || 'Failed to load profile');
              }
            })
            .catch(() => { setAutoRevealing(false); })
            .finally(() => setRevealLoading(false));
        } else {
          // Check if previously purchased
          ExplorerApi.checkSavedProfile(domain).then(data => {
            if (data.has_saved_profile) {
              setAutoRevealing(true);
              setRevealLoading(true);
              ExplorerApi.getFullProfile(slug)
                .then(d => {
                  if (d.success) setFullProfile(d);
                  else {
                    setAutoRevealing(false);
                    setRevealError(d.error || 'Failed to load profile');
                  }
                })
                .catch(() => { setAutoRevealing(false); })
                .finally(() => setRevealLoading(false));
            }
          }).catch(() => {});
        }
      }
    }
  }, [slug]);

  const handleRevealDetails = () => {
    window.trackFeature?.('explorer-reveal');
    setRevealLoading(true);
    setRevealError(null);
    ExplorerApi.getFullProfile(slug)
      .then(data => {
        if (data.success) {
          setFullProfile(data);
          ExplorerApi.getCompanySummary(slug)
            .then(summaryData => {
              if (summaryData.success) setCompanySummary(summaryData);
            });
        }
        else setRevealError(data.error || 'Failed to load profile');
      })
      .catch(err => setRevealError('Failed to load profile. Please try again.'))
      .finally(() => setRevealLoading(false));
  };

  const companyName = companySummary?.company?.name || domain;
  const initialCardProspector = (key) => (
    profileProspectorSheets === undefined
      ? undefined
      : (profileProspectorSheets?.[key] || null)
  );

  return (
    <div className="w-full">
      {/* Explorer Onboarding Widget */}
      {IS_DASHBOARD && window.EXPLORER_ONBOARDING_ENABLED && window.ExplorerOnboardingWidget && (
        <window.ExplorerOnboardingWidget />
      )}

      {/* Search bar - always present */}
      <div className="mb-6">
        <SmartSearchBar />
      </div>

      {/* Company Details - full width */}
      <div className="mb-6">
        <CompanyDetailCard
          companySummary={companySummary}
          summaryLoading={companySummary === null}
          fullProfile={fullProfile}
          onRevealDetails={handleRevealDetails}
          revealLoading={revealLoading}
          revealError={revealError}
          mode={MODE}
          domain={domain}
          autoRevealing={autoRevealing}
        />
      </div>

      {/* Data sections: customers/competitors left (col-span-2), timeline/people-also-viewed right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <CustomersSection
              slug={slug}
              domain={domain}
              companyName={companyName}
              isFreeCompany={isFreeCompany}
              initialProspectorState={initialCardProspector('customers')}
            />
          </div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <CompetitorsSection
              slug={slug}
              domain={domain}
              companyName={companyName}
              isFreeCompany={isFreeCompany}
              initialProspectorState={initialCardProspector('competitors')}
            />
          </div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <FundingSection
              slug={slug}
              domain={domain}
              initialProspectorState={initialCardProspector('funding')}
            />
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <TimelineSection
              slug={slug}
              domain={domain}
              companyName={companyName}
              isFreeCompany={isFreeCompany}
              initialProspectorState={initialCardProspector('updates')}
            />
          </div>
          <PeopleAlsoViewed currentSlug={slug} />
        </div>
      </div>
    </div>
  );
};

// ─── App Root ─────────────────────────────────────────────────────────

const ExplorerApp = () => {
  useExplorerProspectorSocket(EXPLORER_PRODUCT_ID);

  if (INITIAL_PERSON_LOOKUP && Object.values(INITIAL_PERSON_LOOKUP).some(Boolean)) {
    return <PersonLookupPage lookup={INITIAL_PERSON_LOOKUP} />;
  }
  if (INITIAL_PERSON_SLUG) return <PersonProfilePage slug={INITIAL_PERSON_SLUG} />;
  if (INITIAL_DOMAIN) return <ExplorerProfilePage slug={INITIAL_SLUG} domain={INITIAL_DOMAIN} />;
  return <ExplorerSearchPage />;
};

// Mount
const mountEl = document.getElementById('explorer-app');
if (mountEl) {
  ReactDOM.render(<ExplorerApp />, mountEl);
}
